/**
 * 合约订单，委托等业务
 */
import Axios from 'axios';
import { action, computed, observable, runInAction } from 'mobx';
import { BigNumber } from 'bignumber.js';
import cloneDeep from 'lodash/cloneDeep';
import {
  Balance,
  DepthMergeItem,
  DisplayAmountConfig,
  LeverageBracketItem,
  SymbolConfig,
  TickerInfo,
  UserSymbolConfig,
  UserSymbolConfigItem,
} from './data';
import { API, WEBSOCKET_URL_MARKET, WEBSOCKET_URL_USER } from '../config/api';
import UnderLyingEnum from '@/@enums/UnderLyingEnum';
import { PositionItem } from '@/models/position';
import { moveProfitItem, PlanItem, ProfitItem } from '@/models/entrust';
import { OrderItem } from '@/models/order';
import SocketClient from '@/utils/socketClient';
import WebsocketInstance from '@/models/injection/websocketInstance';
import { UpDownThemeEnum } from '@/@enums/Color';
import { getCoinValue, getValue } from '@/utils/digits';
import i18n from '@/i18n';
import { BalanceDetail, CoinBalance } from '@/models/asset';
import { AmountUnitEnum } from '@/@enums/Unit';
import { getDomain, getExchangeUrl, getExchangeOrigin, isSpotPage } from '@/utils/location';
import { isExperienceRecord } from '@/pages/welfareCenter/experienceQuery';
import List from '@/components/header/pcHeader/components/column';
import { applyDepthDeltas } from '@/utils/depthBook';

export interface CreateOrderParams {
  symbol: string;
}

export interface DepthData {
  u: string;
  s?: string;
  t?: number;
  a: [string, string][];
  b: [string, string][];
}

export interface DepthDeltaData {
  id: string;
  s: string;
  ba: number;
  p: string;
  q: string;
}

export interface DealItem {
  a: number; // 成交量
  i?: string; // 成交ID
  m: number | string; // 买卖方向 // 1 == buy == BID, 2 == sell == ASK
  p: number; // 成交价
  s: string; // 交易对
  t: number; //	成交时间
}

export interface PositionConf {
  autoMargin: boolean;
  leverage: number;
  positionModel: PositionMode;
  positionSide: PositionSide;
  positionType: PositionType;
  symbol: string;
  initLeverage: string;
  initPositionModel: PositionMode;
  initPositionType: PositionType;
}
export type TriggerPriceType = 'INDEX_PRICE' | 'MARK_PRICE' | 'LATEST_PRICE';

export interface FollowOrderItem {
  id: string;
  avatar: string; // 头像
  closePrice: string; // 平仓均价
  closeSize: string; // 平仓仓位数量(已平仓位数量)
  closeTime: Date | number; // 平仓时间
  leaderNickname: string; // 交易员
  openLeverage: number; // 杠杆
  openMargin: string; // 开仓初始保证金
  openPrice: string; // 开仓均价
  openSize: string; // 开仓仓位数量
  openTime: Date | number; // 开仓时间
  orderId: string;
  positionSide: PositionSide; // 持仓方向:LONG;SHORT
  positionType: PositionType; // 仓位类型:CROSSED(全仓);ISOLATED(逐仓)
  positionModel?: PositionMode; // 仓位模式:AGGREGATION(合仓);DISAGGREGATION(分仓)
  realizedPnl: string; // 已实现盈亏
  symbolName: string; // 交易对
  trackNo: string; // 跟单trackNo
  triggerPriceType: TriggerPriceType; // 触发价格类型:LATEST_PRICE,MARK_PRICE
  triggerProfitPrice: string; // 止盈价
  triggerStopPrice: string; // 止损价
  availableCloseSize: string; // 可平数量
  positionSize: string; // 持仓数量
  profitRate: number; // 收益率
  // 本地计算
  markPrice?: string;
  floatProfit?: string;
  floatProfitRate?: string;
  floatProfitForMarkPrice?: string;
  liquidationPrice?: string; // 预估强平价

  // 其他配置
  contractSize?: number; // 面值
  quantityPrecision?: number; // 数量精度
  pricePrecision?: number; // 价格精度
  quoteCoinDisplayPrecision?: number; // 计价币种显示精度
  positionId: string;
}

export interface LeaderOrderItem {
  closePrice: string; // 平仓均价
  closeSize: string; // 平仓仓位数量(已平仓位数量)
  closeTime: number; // 平仓时间
  leaderNickname: string; // 交易员
  openLeverage: number; // 杠杆
  openMargin: string; // 开仓初始保证金
  openPrice: string; // 开仓均价
  openSize: string; // 开仓仓位数量
  openTime: number; // 开仓时间
  orderId: string;
  positionSide: PositionSide; // 持仓方向:LONG;SHORT
  positionSize: string;
  positionType: PositionType; // 仓位类型:CROSSED(全仓);ISOLATED(逐仓)
  positionModel?: PositionMode; // 仓位模式:AGGREGATION(合仓);DISAGGREGATION(分仓)
  profit: number; //	收益率,已乘100。（当前带单需前端自行实时计算）
  profitRate: number; //	收益率,已乘100。（当前带单需前端自行实时计算）
  realizedPnl: string; // 已实现盈亏
  symbolName: string; // 交易对
  trackNo: string; // 跟单trackNo
  triggerPriceType: TriggerPriceType; // 触发价格类型:LATEST_PRICE,MARK_PRICE
  triggerProfitPrice: string; // 止盈价
  triggerStopPrice: string; // 止损价
  availableCloseSize: string; // 可平数量
  id: number; //
  positionId: string;
  // 本地计算
  markPrice?: string;
  floatProfit?: string;
  floatProfitRate?: string;
  floatProfitForMarkPrice?: string;
  liquidationPrice?: string; // 预估强平价

  // 其他配置
  contractSize?: number; // 面值
  quantityPrecision?: number; // 数量精度
  pricePrecision?: number; // 价格精度
  quoteCoinDisplayPrecision?: number; // 计价币种显示精度
}

export interface PageInfo {
  page: number;
  size: number;
  total: number;
}

const defaultBuyOrderRowIndex = -1;
const defaultSellOrderRowIndex = 99;

type LiquidationPriceGroupItem = {
  symbol?: string;
  symbolName?: string;
  positionSide?: PositionSide;
  positionType?: PositionType;
  positionModel?: PositionMode;
  liquidationPrice?: string;
};

/**
 * 同交易对、仓位类型和方向存在多笔仓位时统一展示预估强平价：
 * 多仓取各笔计算结果的最大值，空仓取最小值。
 * 例外：逐仓分仓各仓保证金独立，按单仓计算结果展示，不做对齐覆盖。
 */
function alignLiquidationPrices<T extends LiquidationPriceGroupItem>(list: T[]): T[] {
  const groups = new Map<string, T[]>();

  list.forEach(item => {
    const symbol = item.symbol || item.symbolName;
    if (!symbol || !item.positionSide) return;
    // 仅逐仓分仓：各仓独立保证金，禁止统一强平价
    if (item.positionType === 'ISOLATED' && item.positionModel === 'DISAGGREGATION') return;

    const key = `${symbol}|${item.positionType || 'CROSSED'}|${item.positionSide}`;
    const group = groups.get(key) || [];
    group.push(item);
    groups.set(key, group);
  });

  groups.forEach(group => {
    if (group.length < 2) return;

    const validItems = group.filter(item => {
      const price = new BigNumber(item.liquidationPrice || '');
      return !price.isNaN() && price.isFinite() && price.gte(0);
    });
    if (!validItems.length) return;

    const target = validItems.reduce((selected, current) => {
      const selectedPrice = new BigNumber(selected.liquidationPrice || 0);
      const currentPrice = new BigNumber(current.liquidationPrice || 0);
      if (current.positionSide === 'LONG') {
        return currentPrice.gt(selectedPrice) ? current : selected;
      }
      return currentPrice.lt(selectedPrice) ? current : selected;
    });

    group.forEach(item => {
      item.liquidationPrice = target.liquidationPrice;
    });
  });

  return list;
}

export default class Futures {
  private readonly rootStore: any;

  // Snapshots and batches replace the book; do not proxy every price level again.
  @observable.ref depthData: DepthData;

  @observable dealData: DealItem[];

  @observable displayAmountConfig: DisplayAmountConfig; // 盘口, 最新成交展示的数量，会根据orderBook的高度决定；

  @observable limitPrice: string; // 设置限价单的价格

  @observable depthMerge: number;

  @observable isShowCurrentSymbolOnly: boolean;

  symbolSocket: SocketClient; // for 'sub_symbol'

  marketSocket: SocketClient; // for 'sub_ticker', 'sub_mark_price', 'sub_kline'

  @observable userSocket: SocketClient | null;

  @observable buyOrderRowIndex: number;

  @observable sellOrderRowIndex: number;

  @observable positionConf: PositionConf;

  @observable leaderApplyState: {};

  @observable leaderHomeOrderList: LeaderOrderItem[];

  @observable leaderHomeOrderListPageInfo: PageInfo;

  @observable currentFollowOrderList: FollowOrderItem[];

  @observable withdrawFollowOrderList: string[];

  @observable withdrawLeaderOrderList: string[];

  @observable followListRefreshKey: number;

  @observable leaderSymbolList: any[];
  isConfirmAgain: boolean = false;

  constructor(rootStore: any) {
    this.rootStore = rootStore;
    this.depthData = {
      a: [],
      b: [],
      u: '-1',
    };
    this.dealData = [];
    this.symbolSocket = WebsocketInstance(WEBSOCKET_URL_MARKET, 'SYMBOL');
    this.marketSocket = WebsocketInstance(WEBSOCKET_URL_MARKET, 'MARKET');
    this.userSocket = null;
    this.displayAmountConfig = {
      ask: 7,
      bid: 7,
      trade: 7,
    };
    this.limitPrice = '';
    this.depthMerge = 0;
    this.isShowCurrentSymbolOnly = localStorage.getItem('isShowCurrentSymbolOnly') === 'true';
    this.buyOrderRowIndex = defaultBuyOrderRowIndex;
    this.sellOrderRowIndex = defaultSellOrderRowIndex;
    this.positionConf = {
      autoMargin: false,
      leverage: 20,
      positionModel: 'AGGREGATION',
      positionSide: 'LONG',
      positionType: 'CROSSED',
      symbol: '',
      initLeverage: '',
      initPositionModel: 'AGGREGATION',
      initPositionType: 'CROSSED',
    };
    this.leaderApplyState = {};

    this.leaderHomeOrderList = [];
    this.leaderHomeOrderListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };
    this.currentFollowOrderList = [];
    this.withdrawFollowOrderList = [];
    this.withdrawLeaderOrderList = [];
    this.followListRefreshKey = 0;
    this.leaderSymbolList = [];
    this.isConfirmAgain = true;
  }

  readonly balance = observable<string, Balance>(new Map());

  @action.bound
  setBuyOrderRowIndex(value: number) {
    this.buyOrderRowIndex = value;
  }

  @action.bound
  setSellOrderRowIndex(value: number) {
    this.sellOrderRowIndex = value;
  }

  @action.bound
  resetOrderRowIndex() {
    this.buyOrderRowIndex = defaultBuyOrderRowIndex;
    this.sellOrderRowIndex = defaultSellOrderRowIndex;
  }

  @action.bound
  createUserSocket() {
    if (this.userSocket) return;
    this.userSocket = WebsocketInstance(WEBSOCKET_URL_USER, 'USER');
  }

  @computed get currentFee(): { makerFee: number; takerFee: number } {
    const { makerFee: userMakerFee, takerFee: userTakerFee } = this.rootStore.stepRate.userStepRate;
    const { makerFee: configMakerFee, takerFee: configTakerFee } = this.rootStore.ticker.currentSymbolConfig;

    return { makerFee: userMakerFee || configMakerFee, takerFee: userTakerFee || configTakerFee };
  }

  @action.bound
  setDisplayAmountConfig(data: DisplayAmountConfig) {
    this.displayAmountConfig = { ...this.displayAmountConfig, ...data };
  }

  @computed get displayHeight() {
    const { ask, bid, trade } = this.displayAmountConfig;

    return {
      ask: `${ask * 22}px`,
      bid: `${bid * 22}px`,
      trade: `${trade * 22}px`,
    };
  }

  // @loading('future/createOrder')
  // @action
  // async createOrder({ symbol }: CreateOrderParams) {
  //   return Axios.post(API.CREATE_ORDER, {
  //     symbol,
  //   });
  // }
  //
  // @loading('future/getOrder')
  // @action
  // async getOrder({ symbol }: CreateOrderParams) {
  //   const res = await Axios.get(API.CREATE_ORDER, {
  //     symbol,
  //   });
  //
  //   runInAction(() => {
  //     this.balance.set('aa', res.data);
  //   });
  // }

  //
  // 深度
  //

  @action.bound
  setDepthData(data: DepthData) {
    const { s } = data;
    // console.log("deep ----->data:", data);
    // 初始化用
    if (s === '') {
      this.depthData = this.normalizeDepthData(data);
    } else if (s === this.rootStore.ticker.currentSymbolIdentity) {
      this.depthData = this.normalizeDepthData(data);
    }
  }

  @action.bound
  canInsertDepth(u: string) {
    return new BigNumber(u).gt(this.depthData.u);
  }

  @computed get currentDepthMergeStep(): string | undefined {
    return this.currentDepthMergeItem?.label;
  }

  @computed get currentDepthMergeItem(): DepthMergeItem | undefined {
    return this.rootStore.ticker.currentSymbolDepthPrecisionMergeList.find(
      (item: DepthMergeItem) => this.depthMerge === item.value,
    );
  }

  /**
   * 确保depthData的数据中，数量都是大于0的
   * @param params
   */
  @action.bound
  async fetchDepthData(params: { symbol: string; level: number; mergeStep?: string; apply?: boolean }) {
    const { symbol, level, mergeStep, apply = true } = params;

    if (!symbol) return;

    const defaultValue = { s: symbol, t: 0, a: [], b: [], u: '-1' };

    const result: any = await Axios.get(API.TICKER_DEPTH_LIST, {
      params: {
        symbol,
        level,
        ...(mergeStep ? { mergeStep } : {}),
      },
    }).catch(() => {
      if (apply) {
        this.setDepthData(defaultValue);
      }
      return undefined;
    });

    if (result) {
      const { s, t, a, b, u } = result;

      if (Array.isArray(a) && Array.isArray(b)) {
        const normalized = this.normalizeDepthData({
          u,
          s,
          t,
          a,
          b,
        });
        if (apply) {
          this.setDepthData(normalized);
        }
        return normalized;
      } else {
        if (apply) {
          this.setDepthData(defaultValue);
          return defaultValue;
        }
        return undefined;
      }
    } else {
      if (apply) {
        this.setDepthData(defaultValue);
        return defaultValue;
      }
      return undefined;
    }
  }

  /**
   * 更新深度图中的某一档
   * @param data
   * 注意事项：
   * 1. 判断档位的数量是否为0
   * 2. 最大深度
   */
  @action.bound
  setDepthDataByWS(data: DepthData) {
    const currentSymbolIdentity = this.rootStore.ticker.currentSymbolIdentity;
    const { s, a, b, u } = this.depthData;
    const { s: newS, a: newA, b: newB } = data;

    if (currentSymbolIdentity !== newS) {
      return;
    }

    const tempAObj: any = {};
    const tempBObj: any = {};

    if (Array.isArray(a) && Array.isArray(newA)) {
      [...a, ...newA].forEach((item: [string, string]) => {
        tempAObj[item[0]] = item[1];
      });
    }

    if (Array.isArray(b) && Array.isArray(newB)) {
      [...b, ...newB].forEach((item: [string, string]) => {
        tempBObj[item[0]] = item[1];
      });
    }

    this.setDepthData({
      u,
      s,
      a: Object.entries(tempAObj)
        .filter((item: any) => new BigNumber(item[1]).gt(0))
        .sort((i: any, j: any) => {
          // 卖盘从小到大
          const later = new BigNumber(i[0]);
          return later.minus(j[0]).toNumber();
        }) as [string, string][],
      b: Object.entries(tempBObj)
        .filter((item: any) => new BigNumber(item[1]).gt(0))
        .sort((i: any, j: any) => {
          // 买盘从大到小
          const later = new BigNumber(j[0]);
          return later.minus(i[0]).toNumber();
        }) as [string, string][],
    });

    //   const { ba, p, q, s: symbol, id } = data;
    //
    //   if (currentSymbolIdentity !== symbol) {
    //     return;
    //   }
    //
    //   if (!Array.isArray(a) || !Array.isArray(b)) return;
    //
    //   u = id;
    //   let replace = ba === 1 ? tempB : tempA;
    //   const index = replace.findIndex((item: any) => new BigNumber(p).isEqualTo(item[0]));
    //
    //   // 价格档位存在，直接替换
    //   if (index > -1) {
    //     // 数量为0时从深度图去掉
    //     replace.splice(index, 1, [p, q]);
    //   } else {
    //     // 新的价格档位，插入后排序
    //     // TODO：如果达到了深度的上限，是否要截取
    //
    //     replace.push([p, q]);
    //
    //     replace = replace.sort((i: any, j: any) => {
    //       if (ba === 1) {
    //         // 买盘从大到小
    //         const later = new BigNumber(j[0]);
    //         return later.minus(i[0]).toNumber();
    //       }
    //       // 卖盘从小到大
    //       const later = new BigNumber(i[0]);
    //       return later.minus(j[0]).toNumber();
    //     });
    //   }
    //
    //   tempA = ba === 2 ? replace : a;
    //   tempB = ba === 1 ? replace : b;
    //
    // this.setDepthData({
    //   u,
    //   s,
    //   a: tempA.filter(item => new BigNumber(item[1]).gt(0)),
    //   b: tempB.filter(item => new BigNumber(item[1]).gt(0)),
    // });
  }

  @action.bound
  patchDepthDataByWS(data: DepthDeltaData) {
    this.patchDepthBatchByWS([data]);
  }

  @action.bound
  patchDepthBatchByWS(updates: DepthDeltaData[]) {
    if (this.depthData.s !== this.rootStore.ticker.currentSymbolIdentity) return;
    this.depthData = applyDepthDeltas(this.depthData, updates);
  }

  // 所有深度的个数累加，供深度图使用
  @computed get depthSumData() {
    const { a, b } = this.depthData;

    let sumA = new BigNumber(0);
    for (let i = 0; i < a?.length; i += 1) {
      sumA = sumA.plus(a[i][1]);
    }

    let sumB = new BigNumber(0);
    for (let i = 0; i < b?.length; i += 1) {
      sumB = sumB.plus(b[i][1]);
    }

    return {
      a: sumA.valueOf(),
      b: sumB.valueOf(),
      max: BigNumber.max(sumA, sumB).valueOf(),
    };
  }

  @computed get depthDataForFull() {
    const orderList = this.rootStore.order.orderList;
    const { a, b } = this.depthData;
    const depthSumData = this.depthSumData;

    return {
      a: this.buildDepthRows('SELL', a, depthSumData.max, orderList),
      b: this.buildDepthRows('BUY', b, depthSumData.max, orderList),
    };
  }

  // 根据板块高度算出需要展示的深度数据
  @computed get depthDisplayData() {
    const { ask, bid } = this.displayAmountConfig;
    const { a, b } = this.depthData;
    let aResult: any = [];
    let bResult: any = [];

    if (Array.isArray(a)) {
      const aLength = a.length;
      aResult = a.slice(0, Math.min(ask, aLength));
    }

    if (Array.isArray(b)) {
      const bLength = b.length;
      bResult = b.slice(0, Math.min(bid, bLength));
    }

    return {
      a: aResult,
      b: bResult,
    };
  }

  // 展示的深度张数累加，用于计算盘口的背景百分比
  @computed get depthDisplaySumData() {
    const { a, b } = this.depthDisplayData;

    let sumA = new BigNumber(0);
    for (let i = 0; i < a?.length; i += 1) {
      sumA = sumA.plus(a[i][1]);
    }

    let sumB = new BigNumber(0);
    for (let i = 0; i < b?.length; i += 1) {
      sumB = sumB.plus(b[i][1]);
    }

    return {
      a: sumA.valueOf(),
      b: sumB.valueOf(),
      max: BigNumber.max(sumA, sumB).valueOf(),
    };
  }

  @action.bound
  buildDepthRows(side: OrderSide, data: [string, string][], sumData: string, orderList: OrderItem[]) {
    const orderPriceSet = this.getOrderPriceSetForDepth(side, orderList);
    let amountSum = new BigNumber(0);

    return (data || []).map((item: [string, string]) => {
      const currentPrice = item[0];
      const currentAmount = new BigNumber(item[1]);
      amountSum = amountSum.plus(currentAmount);

      const computedRate = sumData
        ? amountSum
          .div(new BigNumber(sumData))
          .times(100)
          .toNumber()
        : 0;

      return [
        currentPrice,
        currentAmount.toFixed(0),
        amountSum.toFixed(0),
        computedRate > 100 ? 100 : computedRate,
        orderPriceSet.has(this.normalizePriceKey(currentPrice)),
      ];
    });
  }

  private normalizeDepthData(data: Partial<DepthData>): DepthData {
    const normalizeSide = (side: [string, string][], bidSide: boolean): [string, string][] =>
      (Array.isArray(side) ? side : [])
        .filter((item: [string, string]) => new BigNumber(item[1]).gt(0))
        .map(item => ({ item, price: new BigNumber(item[0]) }))
        .sort((left, right) => bidSide ? right.price.comparedTo(left.price) : left.price.comparedTo(right.price))
        .map(({ item }) => item);

    return {
      u: data.u || '-1',
      s: data.s || '',
      t: data.t || 0,
      a: normalizeSide(data.a || [], false),
      b: normalizeSide(data.b || [], true),
    };
  }

  private normalizePriceKey(price: string | number) {
    return new BigNumber(price).toFixed();
  }

  private formatOrderPrice(orderSide: OrderSide, price: string | number) {
    const mergeItem = this.currentDepthMergeItem;

    if (!mergeItem) {
      return this.normalizePriceKey(price);
    }

    if (orderSide === 'BUY') {
      return this.normalizePriceKey(
        new BigNumber(price)
          .idiv(mergeItem.base)
          .times(mergeItem.base)
          .toFixed(mergeItem.precision),
      );
    }

    return this.normalizePriceKey(
      new BigNumber(new BigNumber(price).div(mergeItem.base).toFixed(0, 0))
        .times(mergeItem.base)
        .toFixed(mergeItem.precision),
    );
  }

  private getOrderPriceSetForDepth(side: OrderSide, orderList: OrderItem[]) {
    return new Set(
      orderList
        .filter((item: OrderItem) => item.orderSide === side)
        .map((item: OrderItem) => this.formatOrderPrice(item.orderSide, item.price)),
    );
  }

  /**
   * return: [price, amount, total, rate, isOrderPosition]
   */
  @computed get depthDisplayDataForFull() {
    const orderList = this.rootStore.order.orderList;
    const { a, b } = this.depthDisplayData;
    const depthDisplaySumData = this.depthDisplaySumData;

    return {
      a: this.buildDepthRows('SELL', a, depthDisplaySumData.max, orderList),
      b: this.buildDepthRows('BUY', b, depthDisplaySumData.max, orderList),
    };
  }

  //
  // 最新成交
  //

  @action.bound
  setDealData(data: DealItem[]) {
    const firstItem = data[0];

    if (firstItem) {
      const { s } = firstItem;

      if (s === this.rootStore.ticker.currentSymbolIdentity) {
        this.dealData = data;
      }
    } else {
      this.dealData = data;
    }
  }

  @action.bound
  async fetchDealData(params: { symbol: string; num?: number }) {
    const { symbol, num = 30 } = params;

    if (!symbol) return;

    const result: any = await Axios.get(API.TICKER_TRADE_LATEST_LIST, {
      params: {
        symbol,
        num,
      },
    });

    if (result) {
      this.setDealData(result);
    }
  }

  /**
   * 添加新的成交记录
   * 一般是websocket推送
   * @param data
   * 注意事项
   * 1. 最大记录
   */
  @action.bound
  setDealDataByWS(data: DealItem[]) {
    const currentSymbolIdentity = this.rootStore.ticker.currentSymbolIdentity;

    if (Array.isArray(data) && data.length > 0) {
      const { s } = data[0];

      if (s !== currentSymbolIdentity) {
        return;
      }

      // TODO: 需要知道最大显示的数量
      this.setDealData([...data, ...this.dealData].slice(0, 30));
    }
  }

  @computed get valuationUnit() {
    const { underlying, ticker } = this.rootStore;
    const { currentSymbolConfig } = ticker;
    const { currentUnderlying } = underlying;
    return currentUnderlying === UnderLyingEnum.U_BASED ? 'USDT' : currentSymbolConfig?.baseCoin?.toUpperCase();
  }

  @computed get priceUnit() {
    return this.rootStore.underlying.currentUnderlying === UnderLyingEnum.U_BASED ? 'USDT' : 'USD';
  }

  @action.bound
  getFloatProfit(position: PositionItem) {
    // console.log('======getFloatProfit======',position);
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const symbolMarkPriceMap = this.rootStore.ticker.markPriceMap;
    const symbolCurrentPriceMap = this.rootStore.ticker.symbolCurrentPriceMap;
    const getSymbolUnderlyingType = this.rootStore.ticker.getSymbolUnderlyingType;

    const { symbol, entryPrice, positionSize, positionSide } = position;
    const symbolConfig = getSymbolConfig(symbol);
    // 最新价口径：优先使用最新价，没有则回退标记价
    const markPrice = symbolCurrentPriceMap[symbol] || symbolMarkPriceMap[symbol];
    const underlyingType = getSymbolUnderlyingType(symbol);
    const { contractSize } = symbolConfig;

    if (markPrice && contractSize) {
      let floatProfit = new BigNumber(0);

      const base = new BigNumber(positionSize).times(contractSize);
      if (underlyingType === UnderLyingEnum.U_BASED) {
        if (positionSide === 'LONG') {
          floatProfit = new BigNumber(markPrice).minus(entryPrice).times(base);
        } else if (positionSide === 'SHORT') {
          floatProfit = new BigNumber(entryPrice).minus(markPrice).times(base);
        }
      } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
        // 浮动盈亏
        if (positionSide === 'LONG') {
          floatProfit = new BigNumber(1)
            .div(entryPrice)
            .minus(new BigNumber(1).div(markPrice))
            .times(base);
        } else if (positionSide === 'SHORT') {
          floatProfit = new BigNumber(1)
            .div(markPrice)
            .minus(new BigNumber(1).div(entryPrice))
            .times(base);
        }
      }

      return floatProfit.valueOf();
    }

    return '0';
  }

  //
  // 持仓板块，列表
  //

  @computed get positionListWithConfig() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const getPrecision = this.rootStore.ticker.getPrecision;
    const adlMap = this.rootStore.position.adlMap;
    const positionList = cloneDeep(this.rootStore.position.positionList);
    const getPositionBracket = this.rootStore.ticker.getPositionBracket;
    const getSymbolUnderlyingType = this.rootStore.ticker.getSymbolUnderlyingType;
    const getLiquidationPrice = this.getLiquidationPrice;
    const getFloatProfit = this.getFloatProfit;
    // 过虑仓位>0的仓位，且balanceType !== 'COPY'
    const positionListWithConfig = positionList.filter((item: PositionItem) => new BigNumber(item.positionSize).gt(0) && item.balanceType !== 'COPY');
    if (!positionListWithConfig.length) return [];
    // getFloatProfit is an action, so its price reads are untracked. Keep the
    // latest-price dependency here for live PnL whenever positions exist.
    void this.rootStore.ticker.symbolCurrentPriceMap;
    const calculatedList = positionListWithConfig.map((item: PositionItem) => {
      const { maintMarginRate } = getPositionBracket(item);
      // alert(item.unsettledProfit)
      // 添加本地计算值
      item.floatProfit = '0.00';
      item.floatProfitRate = '0.00';
      item.adl = 0;
      item.initialMargin = '0.00';
      item.liquidationPrice = getLiquidationPrice(item, positionListWithConfig);
      item.maintMargin = '0.00';
      const { symbol, entryPrice, positionSize, isolatedMargin, positionSide, leverage, positionId } = item;

      const symbolConfig = getSymbolConfig(symbol);
      const markPrice = this.rootStore.ticker.markPriceMap[symbol];

      // eslint-disable-next-line no-useless-concat
      // console.log('[markPrice]' + 'symbol' + symbol + ' | ' + markPrice);
      const adl = adlMap[positionId];

      if (symbolConfig) {
        const { contractSize, pricePrecision, quoteCoinDisplayPrecision } = symbolConfig;
        const { quantity: quantityPrecision } = getPrecision(symbol);
        const underlyingType = getSymbolUnderlyingType(symbol);

        // 添加其他配置信息
        item.contractSize = contractSize;
        item.quantityPrecision = quantityPrecision; // 数量精度
        item.pricePrecision = pricePrecision; // 价格精度
        item.quoteCoinDisplayPrecision = quoteCoinDisplayPrecision; // 计价币种精度

        // 浮动盈亏
        // U本位做多：数量(张) * 合约面值(张) * (标记价格-开仓均价)；
        // U本位做空：数量(张) * 合约面值(张) * (开仓均价-标记价格)；
        // 币本位做多：数量(张) * 合约面值(张) * (1 / 开仓均价 - 1 / 标记价格)
        // 币本位做空：数量(张) * 合约面值(张) * (1 / 标记价格 - 1 / 开仓均价)
        // 收益率 = 未实现盈亏 / 仓位保证金

        if (markPrice && contractSize) {
          if (underlyingType === UnderLyingEnum.U_BASED) {
            item.initialMargin = new BigNumber(entryPrice)
              .times(positionSize)
              .times(contractSize)
              .div(leverage)
              .valueOf();

            if (maintMarginRate) {
              item.maintMargin = new BigNumber(entryPrice)
                .times(positionSize)
                .times(contractSize)
                .times(maintMarginRate)
                .valueOf();
            }
          } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
            // 卖出开空：数量(张) * 合约面值(张) / (卖出委托价格 * 起始保证金率)；
            item.initialMargin = new BigNumber(positionSize)
              .times(contractSize)
              .div(entryPrice)
              .div(leverage)
              .valueOf();

            if (maintMarginRate) {
              item.maintMargin = new BigNumber(positionSize)
                .times(contractSize)
                .div(entryPrice)
                .times(maintMarginRate)
                .valueOf();
            }
          }

          const floatProfit = getFloatProfit(item);
          item.floatProfit = floatProfit;
          // console.log('floatProfit', floatProfit);
          // console.log('isolatedMargin', isolatedMargin);
          item.floatProfitRate = new BigNumber(floatProfit)
            .div(isolatedMargin)
            .times(100)
            .valueOf();
          if (adl) {
            item.adl = adl.quantile;
          }
        }
      }

      return item;
    });
    return alignLiquidationPrices(calculatedList);
  }

  // 有持仓数的持仓
  @computed get positionListWithValue() {
    return this.positionListWithConfig.filter((item: PositionItem) => new BigNumber(item.positionSize).gt(0));
  }

  // 合约主账户：所有全仓仓位 unsettledProfit 汇总（不含 COPY 跟单仓位）
  @computed get crossedUnsettledProfitTotal(): string {
    const positionList = this.rootStore.position.positionList;
    let total = new BigNumber(0);
    positionList.forEach((p: PositionItem) => {
      if (
        p.positionType === 'CROSSED' &&
        p.balanceType !== 'COPY' &&
        new BigNumber(p.positionSize).gt(0)
      ) {
        total = total.plus(p.unsettledProfit || 0);
      }
    });
    return total.valueOf();
  }

  // 跟单账户：所有全仓仓位 unsettledProfit 汇总（仅 COPY 跟单仓位）
  @computed get copyCrossedUnsettledProfitTotal(): string {
    const positionList = this.rootStore.position.positionList;
    let total = new BigNumber(0);
    positionList.forEach((p: PositionItem) => {
      if (
        p.positionType === 'CROSSED' &&
        p.balanceType === 'COPY' &&
        new BigNumber(p.positionSize).gt(0)
      ) {
        total = total.plus(p.unsettledProfit || 0);
      }
    });
    return total.valueOf();
  }

  // 所有有持仓数的持仓
  @computed get allPositionListWithValue() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const symbolMarkPriceMap = this.rootStore.ticker.markPriceMap;
    const positionList = cloneDeep(this.rootStore.position.allPositionList);

    const getFloatProfit = this.getFloatProfit;

    return positionList
      .filter((item: PositionItem) => new BigNumber(item.positionSize).gt(0))
      .map((item: PositionItem) => {
        // 添加本地计算值
        item.floatProfit = '0.00';

        const { symbol } = item;

        const symbolConfig = getSymbolConfig(symbol);
        const markPrice = symbolMarkPriceMap[symbol];

        if (symbolConfig) {
          const { contractSize } = symbolConfig;

          if (markPrice && contractSize) {
            item.floatProfit = getFloatProfit(item);
          }
        }

        return item;
      });
  }

  @computed get profitListWithConfig() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const targetList = cloneDeep(this.rootStore.entrust.profitList);
    const positionListWithConfig = this.positionListWithConfig;

    return targetList.map((item: ProfitItem) => {
      // 添加本地计算值
      item.liquidationPrice =
        positionListWithConfig.find((p: ProfitItem) => p.symbol === item.symbol && p.positionSide === item.positionSide)
          ?.liquidationPrice || '--';

      const { symbol } = item;
      const symbolConfig = getSymbolConfig(symbol);

      if (symbolConfig) {
        const { contractSize, pricePrecision } = symbolConfig;

        // 添加其他配置信息
        item.contractSize = contractSize;
        item.pricePrecision = pricePrecision;
      }

      return item;
    });
  }
  @computed get moveProfitListWithConfig() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const targetList = cloneDeep(this.rootStore.entrust.moveProfitList);
    const positionListWithConfig = this.positionListWithConfig;

    return targetList.map((item: moveProfitItem) => {
      // 添加本地计算值
      item.moveProfitPrice =
        positionListWithConfig.find((p: moveProfitItem) => p.symbol === item.symbol && p.positionSide === item.positionSide)
          ?.moveProfitPrice || '--';

      const { symbol } = item;
      const symbolConfig = getSymbolConfig(symbol);

      if (symbolConfig) {
        const { contractSize } = symbolConfig;

        // 添加其他配置信息
        item.contractSize = contractSize;
      }

      return item;
    });
  }
  @computed get orderListWithConfig() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const targetList = cloneDeep(this.rootStore.order.orderList);

    return targetList.map((item: OrderItem) => {
      const { symbol } = item;
      const symbolConfig = getSymbolConfig(symbol);

      if (symbolConfig) {
        const { contractSize } = symbolConfig;

        // 添加其他配置信息
        item.contractSize = contractSize;
      }

      return item;
    });
  }

  // 当前交易对限价委托,持仓数可能为0
  @computed get currentSymbolActiveOrder(): { long: OrderItem[]; short: OrderItem[] } {
    const currentSymbol = this.rootStore.ticker.currentSymbolIdentity;
    const orderListWithConfig = this.orderListWithConfig;

    return {
      long: orderListWithConfig.filter(
        (item: OrderItem) => currentSymbol === item.symbol && item.positionSide === 'LONG',
      ),
      short: orderListWithConfig.filter(
        (item: OrderItem) => currentSymbol === item.symbol && item.positionSide === 'SHORT',
      ),
    };
  }

  // TODO: 订单名义价值的替代计算方式，稳定后删除
  // 当前交易对未成交订单价值
  // 注意要减去已成交的数量
  // @computed get currentSymbolOrderValueOfActive(): { long: string; short: string } {
  //   const { long: longOrder, short: shortOrder } = this.currentSymbolActiveOrder;
  //   let longResult = new BigNumber(0);
  //   let shortResult = new BigNumber(0);
  //
  //   longOrder.forEach((item: OrderItem) => {
  //     longResult = longResult.plus(
  //       getValue(
  //         item.price,
  //         new BigNumber(item?.origQty || 0).minus(item?.executedQty || 0).valueOf(),
  //         item.contractSize,
  //       ),
  //     );
  //   });
  //
  //   shortOrder.forEach((item: OrderItem) => {
  //     shortResult = shortResult.plus(
  //       getValue(
  //         item.price,
  //         new BigNumber(item?.origQty || 0).minus(item?.executedQty || 0).valueOf(),
  //         item.contractSize,
  //       ),
  //     );
  //   });
  //
  //   return {
  //     long: longResult.valueOf(),
  //     short: shortResult.valueOf(),
  //   };
  // }

  @computed get orderRecordWithConfig() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const targetList = cloneDeep(this.rootStore.order.orderRecord);

    return targetList.map((item: OrderItem) => {
      const { symbol } = item;
      const symbolConfig = getSymbolConfig(symbol);

      if (symbolConfig) {
        const { contractSize } = symbolConfig;

        // 添加其他配置信息
        item.contractSize = contractSize;
      }

      return item;
    });
  }

  @computed get planListWithConfig() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const targetList = cloneDeep(this.rootStore.entrust.planList);

    return targetList.map((item: PlanItem) => {
      const { symbol } = item;
      const symbolConfig = getSymbolConfig(symbol);

      if (symbolConfig) {
        const { contractSize } = symbolConfig;

        // 添加其他配置信息
        item.contractSize = contractSize;
      }

      return item;
    });
  }

  @action.bound
  generateBalanceData(origin: BalanceDetail, positionList: PositionItem[]) {
    const result: CoinBalance = { ...origin } as CoinBalance;

    const { coin, walletBalance, assetBalance, availableBalance, isolatedMargin, crossedMargin, bonus } = origin;
    let floatProfit = new BigNumber(0);
    // 该结算货币下的全仓未实现盈亏
    let crossedFloatProfit = new BigNumber(0);
    // 全仓仓位未实现亏损
    let crossedFloatLoss = new BigNumber(0);

    if (coin === 'usdt') {
      positionList.forEach((p: PositionItem) => {
        if (p.symbol?.split('_')[1] === 'usdt') {
          const fp = p.floatProfit || 0;
          floatProfit = floatProfit.plus(fp);

          if (p.positionType === 'CROSSED') {
            crossedFloatProfit = crossedFloatProfit.plus(fp);

            if (new BigNumber(fp).lt(0)) {
              crossedFloatLoss = crossedFloatLoss.plus(fp);
            }
          }
        }
      });
    } else {
      positionList.forEach((p: PositionItem) => {
        if (p.symbol?.split('_')[0] === coin) {
          const fp = p.floatProfit || 0;
          floatProfit = floatProfit.plus(fp);

          if (p.positionType === 'CROSSED') {
            crossedFloatProfit = crossedFloatProfit.plus(fp);

            if (new BigNumber(fp).lt(0)) {
              crossedFloatLoss = crossedFloatLoss.plus(fp);
            }
          }
        }
      });
    }

    result.floatProfit = floatProfit.valueOf();
    // 资产总览 = 包含体验金的资产余额 + 未实现盈亏
    result.equity = new BigNumber(assetBalance ?? walletBalance).plus(floatProfit).valueOf();
    // 可用余额 = max(0，真实可用 + ∑该结算货币下的全仓未实现盈亏 + 体验金)
    result.availableBalanceDisplay = BigNumber.max(
      0,
      new BigNumber(availableBalance).plus(crossedFloatProfit).plus(bonus || 0),
    ).valueOf();
    // 净资产余额 = max(0，真实可用 - abs(∑全仓仓位未实现亏损))
    result.netBalance = BigNumber.max(0, new BigNumber(availableBalance).minus(crossedFloatLoss.abs())).valueOf();
    // 仓位保证金 = ∑逐仓仓位保证金 + ∑全仓仓位起始保证金
    result.positionMargin = new BigNumber(isolatedMargin).plus(crossedMargin).valueOf();
    // console.log(result)
    return result;
  }

  @computed get balanceData(): CoinBalance {
    const balance = this.rootStore.asset.balance;
    const positionListWithValue = this.positionListWithValue;
    return this.generateBalanceData(balance, positionListWithValue);
  }

  @computed get displayCoinBalanceData(): CoinBalance {
    const allPositionListWithValue = this.allPositionListWithValue;
    const displayCoinBalance = this.rootStore.asset.displayCoinBalance;

    return displayCoinBalance.coin
      ? this.generateBalanceData(displayCoinBalance, allPositionListWithValue)
      : this.balanceData;
  }

  // 当前交易对持仓（仅自有资金；体验仓独立体系，不参与模式/杠杆/开仓计算）
  @computed get currentSymbolPosition(): { long: PositionItem; short: PositionItem } {
    const currentSymbol = this.rootStore.ticker.currentSymbolIdentity;
    const positionListWithConfig = this.positionListWithConfig;

    return {
      long: positionListWithConfig.find(
        (item: PositionItem) =>
          currentSymbol === item.symbol &&
          item.positionSide === 'LONG' &&
          !isExperienceRecord(item),
      ),
      short: positionListWithConfig.find(
        (item: PositionItem) =>
          currentSymbol === item.symbol &&
          item.positionSide === 'SHORT' &&
          !isExperienceRecord(item),
      ),
    };
  }

  // 当前交易对持仓价值
  @computed get currentSymbolPositionValue(): { long: string; short: string } {
    const { long: longPosition, short: shortPosition } = this.currentSymbolPosition;
    const currentUnderlying = this.rootStore.underlying.currentUnderlying;

    const long = longPosition
      ? currentUnderlying === UnderLyingEnum.U_BASED
        ? getValue(longPosition.entryPrice, longPosition.positionSize, longPosition.contractSize)
        : getCoinValue(longPosition.entryPrice, longPosition.positionSize, longPosition.contractSize)
      : '0';
    const short = shortPosition
      ? currentUnderlying === UnderLyingEnum.U_BASED
        ? getValue(shortPosition.entryPrice, shortPosition.positionSize, shortPosition.contractSize)
        : getCoinValue(shortPosition.entryPrice, shortPosition.positionSize, shortPosition.contractSize)
      : '0';

    return {
      long,
      short,
    };
  }

  // 当前交易对订单（排除体验仓委托）
  @computed get currentSymbolOrder(): { long: OrderItem[]; short: OrderItem[] } {
    const currentSymbol = this.rootStore.ticker.currentSymbolIdentity;
    const orderList = this.rootStore.order.orderList;

    return {
      long: orderList.filter(
        (item: OrderItem) =>
          currentSymbol === item.symbol &&
          item.positionSide === 'LONG' &&
          !isExperienceRecord(item as any),
      ),
      short: orderList.filter(
        (item: OrderItem) =>
          currentSymbol === item.symbol &&
          item.positionSide === 'SHORT' &&
          !isExperienceRecord(item as any),
      ),
    };
  }

  // 当前交易对订单名义价值
  // U本位
  // 1. 多仓订单名义价值 = 订单占用保证金 / (初始保证金 + 2 * taker费率)
  // 2. 空仓订单名义价值 = 订单占用保证金 / (起始保证金率 * (1 + Taker费率) + Taker费率 * (1 - 维持保证金率))
  // 币本位
  // 多仓：订单名义价值 = 订单占用保证金 / (起始保证金率 * (1 + Taker费率) + Taker费率 * (1 - 维持保证金率【杠杆倍数】))
  // 空仓：订单名义价值 = 订单占用保证金 / (起始保证金率 + 2 * Taker费率)
  @computed get currentSymbolOrderValue(): { long: string; short: string } {
    const { long: longOrder, short: shortOrder } = this.currentSymbolOrder;
    const { long: longConfig, short: shortConfig } = this.userCurrentSymbolConfig;
    const { takerFee } = this.currentFee;
    const getLeverageMaxBracket = this.rootStore.ticker.getLeverageMaxBracket;
    const isUBased = this.rootStore.underlying.isUBased;
    const longLeverage = longConfig.leverage;
    const shortLeverage = shortConfig.leverage;

    let longResult = new BigNumber(0);
    let shortResult = new BigNumber(0);
    if (isUBased) {
      longOrder.forEach((item: OrderItem) => {
        const value = new BigNumber(item.marginFrozen).div(
          new BigNumber(1).div(longLeverage).plus(new BigNumber(takerFee).times(2)),
        );
        longResult = longResult.plus(value);
      });

      shortOrder.forEach((item: OrderItem) => {
        const value = new BigNumber(item.marginFrozen).div(
          new BigNumber(1)
            .div(shortLeverage)
            .times(new BigNumber(1).plus(takerFee))
            .plus(new BigNumber(1).minus(getLeverageMaxBracket(shortLeverage)?.maintMarginRate || 0).times(takerFee)),
        );
        shortResult = shortResult.plus(value);
      });
    } else {
      longOrder.forEach((item: OrderItem) => {
        const value = new BigNumber(item.marginFrozen).div(
          new BigNumber(1)
            .div(longLeverage)
            .times(new BigNumber(1).plus(takerFee))
            .plus(new BigNumber(1).minus(getLeverageMaxBracket(longLeverage)?.maintMarginRate || 0).times(takerFee)),
        );
        longResult = longResult.plus(value);
      });

      shortOrder.forEach((item: OrderItem) => {
        const value = new BigNumber(item.marginFrozen).div(
          new BigNumber(1).div(shortLeverage).plus(new BigNumber(takerFee).times(2)),
        );
        shortResult = shortResult.plus(value);
      });
    }

    return {
      long: longResult.isNaN() ? '0' : longResult.valueOf(),
      short: shortResult.isNaN() ? '0' : shortResult.valueOf(),
    };
  }

  // 当前交易对计划委托（排除体验仓）
  @computed get currentSymbolPlan(): { long: PlanItem[]; short: PlanItem[] } {
    const currentSymbol = this.rootStore.ticker.currentSymbolIdentity;
    const planList = this.rootStore.entrust.planList;

    return {
      long: planList.filter(
        (item: PlanItem) =>
          currentSymbol === item.symbol &&
          item.positionSide === 'LONG' &&
          !isExperienceRecord(item as any),
      ),
      short: planList.filter(
        (item: PlanItem) =>
          currentSymbol === item.symbol &&
          item.positionSide === 'SHORT' &&
          !isExperienceRecord(item as any),
      ),
    };
  }

  // 交易对是否有持仓和委托
  @computed get isCleanSymbol() {
    const currentSymbolPosition = this.currentSymbolPosition;
    const currentSymbolOrder = this.currentSymbolOrder;
    const currentSymbolPlan = this.currentSymbolPlan;

    const result = {
      long: true,
      short: true,
    };

    if (
      (currentSymbolPosition.long && new BigNumber(currentSymbolPosition.long.positionSize).gt(0)) ||
      (currentSymbolOrder.long && currentSymbolOrder.long.length > 0) ||
      (currentSymbolPlan.long && currentSymbolPlan.long.length > 0)
    ) {
      result.long = false;
    }

    if (
      (currentSymbolPosition.short && new BigNumber(currentSymbolPosition.short.positionSize).gt(0)) ||
      (currentSymbolOrder.short && currentSymbolOrder.short.length > 0) ||
      (currentSymbolPlan.short && currentSymbolPlan.short.length > 0)
    ) {
      result.short = false;
    }

    return result;
  }

  @action.bound
  setLimitPrice(value: string) {
    this.limitPrice = value;
  }

  @computed get priceSide() {
    const upDownTheme = this.rootStore.ui.upDownTheme;
    const length = this.dealData.length;
    const result: any = {
      side: '',
      className: '',
      latestPrice: '',
      prevPrice: '',
    };

    if (length > 1) {
      const latestTrade1 = this.dealData[0];
      const latestTrade2 = this.dealData[1];
      const { p: p1 } = latestTrade1;
      const { p: p2 } = latestTrade2;

      result.latestPrice = p1;
      result.prevPrice = p2;

      if (new BigNumber(p1).gt(p2)) {
        result.side = 'UP';
        result.className = upDownTheme === UpDownThemeEnum.greenUpRedDown ? 'text-green' : 'text-red';
      } else if (new BigNumber(p1).lt(p2)) {
        result.side = 'DOWN';
        result.className = upDownTheme === UpDownThemeEnum.greenUpRedDown ? 'text-red' : 'text-green';
      }
    }

    return result;
  }

  // 根据用户持仓信息得到所处的风险限额档位；
  // 存储用户交易对所处档位的多空持仓的杠杆倍数，维持保证金，最大杠杆，最小杠杆等
  @computed get userCurrentSymbolConfig(): UserSymbolConfig {
    const symbol = this.rootStore.ticker.currentSymbolIdentity;
    const leverageBracketList = this.rootStore.ticker.currentSymbolLeverageBrackets;
    const symbolConfig = this.rootStore.ticker.currentSymbolConfig;
    const positionList = this.rootStore.position.positionList;
    const latestMarkPrice = this.rootStore.ticker.latestMarkPrice;
    const positionConf = this.positionConf;

    const { contractSize } = symbolConfig;

    const longPosition = positionList.find(
      (item: PositionItem) => item.symbol === symbol && item.positionSide === 'LONG',
    );
    const shortPosition = positionList.find(
      (item: PositionItem) => item.symbol === symbol && item.positionSide === 'SHORT',
    );

    const longSize = longPosition?.positionSize || 0;
    const shortSize = shortPosition?.positionSize || 0;
    const longBracket = leverageBracketList.find((item: LeverageBracketItem) =>
      new BigNumber(new BigNumber(latestMarkPrice).times(longSize).times(contractSize)).lte(item.maxNominalValue),
    );
    const shortBracket = leverageBracketList.find((item: LeverageBracketItem) =>
      new BigNumber(new BigNumber(latestMarkPrice).times(shortSize).times(contractSize)).lte(item.maxNominalValue),
    );

    const createBrakcet = (position: PositionItem, bracket: LeverageBracketItem) => {
      const { maxNominalValue, initLeverage = 20, basicMaintMarginRate, initPositionType } = symbolConfig;
      const maxAmount = bracket?.maxNominalValue
        ? new BigNumber(bracket.maxNominalValue)
          .dividedBy(contractSize)
          .idiv(latestMarkPrice)
          .valueOf()
        : new BigNumber(maxNominalValue)
          .dividedBy(contractSize)
          .idiv(latestMarkPrice)
          .valueOf();

      const positionSize = position?.positionSize || 0;

      return {
        leverage: positionConf?.leverage || positionConf.initLeverage,
        positionType: position?.positionType || initPositionType,
        maxLeverage: bracket ? bracket.maxLeverage : 100,
        minLeverage: bracket ? bracket.minLeverage : 1,
        maintMarginRate: bracket ? bracket.maintMarginRate : basicMaintMarginRate,
        maxNominalValue: bracket ? bracket.maxNominalValue : maxNominalValue,
        maxAmount,
        availableQty: bracket ? new BigNumber(maxAmount).minus(positionSize).toNumber() : maxNominalValue,
        positionSize,
      } as UserSymbolConfigItem;
    };

    return {
      symbol,
      long: createBrakcet(longPosition, longBracket),
      short: createBrakcet(shortPosition, shortBracket),
    };
  }

  // 深度合并
  @action.bound
  setDepthMerge(value: number) {
    const depthMergeObj = this.rootStore.ticker.currentSymbolDepthPrecisionMergeList.find(
      (item: DepthMergeItem) => value === item.value,
    );

    let v = value;

    // 如果精度做了调整，本地缓存的的深度不符合要求时
    if (!depthMergeObj) {
      v = 1;
    }

    this.depthMerge = v;
    const depthMergeStorage = localStorage.getItem('depthMerge');
    const obj = JSON.parse(depthMergeStorage || '{}');
    obj[this.rootStore.ticker.currentSymbolIdentity] = v;
    localStorage.setItem('depthMerge', JSON.stringify(obj));
  }

  @action.bound
  setIsShowCurrentSymbolOnly(value: boolean) {
    this.isShowCurrentSymbolOnly = value;
    localStorage.setItem('isShowCurrentSymbolOnly', `${value}`);
  }
  // 最新价格是取的成交价格的第一条，如果成交价格为空，则取当前symbol的最新价格没
  // 没有成交价格就取socket的最新价格c
  @computed get latestPrice() {
    const dealData = this.dealData;

    if (Array.isArray(dealData) && dealData.length > 0) {
      return dealData[0].p;
    }

    return this.rootStore.ticker.currentSymbolTicker.c;
  }

  @action.bound
  getLatestPrice() {
    const dealData = this.dealData;
    if (Array.isArray(dealData) && dealData.length > 0) {
      return dealData[0].p ?? '';
    }
    return this.rootStore.ticker.currentSymbolTicker.c ?? '';
  }

  @action.bound
  getUnitAmount(
    amount: string,
    symbol: string,
    price: string,
    underlyingType?: UnderLyingEnum,
  ): { amount: string; unitView: string } {
    const config = this.rootStore.ticker.getSymbolConfig(symbol);
    const precision = this.rootStore.ticker.getPrecision(symbol);

    if (!(config && precision) || new BigNumber(price).lte(0)) {
      return {
        amount: '--',
        unitView: '',
      };
    }

    let unit = this.rootStore.ui.unit;
    const uBasedUnit = this.rootStore.ui.uBasedUnit;
    const coinBasedUnit = this.rootStore.ui.coinBasedUnit;

    if (underlyingType) {
      unit = underlyingType === UnderLyingEnum.U_BASED ? uBasedUnit : coinBasedUnit;
    }
    switch (unit) {
      case AmountUnitEnum.sheet:
        return {
          amount,
          unitView: i18n.t('unit.sheet'),
        };
      case AmountUnitEnum.usdt:
        return {
          amount: new BigNumber(amount)
            .times(config.contractSize)
            .times(price)
            .toFixed(precision.quoteCoinDisplay, 1),
          unitView: 'USDT',
        };
      case AmountUnitEnum.usd:
        return {
          amount: new BigNumber(amount).times(config.contractSize).toFixed(precision.quoteCoinDisplay, 1),
          unitView: 'USD',
        };
      default: {
        let amountBN: any = '';

        if (
          (underlyingType && underlyingType === UnderLyingEnum.U_BASED) ||
          (!underlyingType && this.rootStore.underlying.isUBased)
        ) {
          amountBN = new BigNumber(amount).times(config.contractSize);
        } else {
          amountBN = new BigNumber(amount).times(config.contractSize).div(price);
        }

        return amountBN.isNaN()
          ? {
            amount: '--',
            unitView: '',
          }
          : {
            amount: amountBN.toFixed(precision.quantity, 1),
            unitView: symbol.split('_')[0]?.toUpperCase(),
          };
      }
    }
  }

  @action.bound
  getUnitAmountCoin(
    amount: string,
    symbol: string,
    price: string,
    underlyingType?: UnderLyingEnum,
  ): { amount: string; unitView: string } {
    const config = this.rootStore.ticker.getSymbolConfig(symbol);
    const precision = this.rootStore.ticker.getPrecision(symbol);

    if (!(config && precision) || new BigNumber(price).lte(0)) {
      return {
        amount: '--',
        unitView: '',
      };
    }

    let amountBN: any = '';

    if (underlyingType === UnderLyingEnum.COIN_BASED) {
      amountBN = new BigNumber(amount).times(config.contractSize);
    } else {
      return {
        amount: new BigNumber(amount)
          .times(config.contractSize)
          .times(price)
          .toFixed(precision.quoteCoinDisplay, 1),
        unitView: 'USDT',
      };
    }

    return amountBN.isNaN()
      ? {
        amount: '--',
        unitView: '',
      }
      : {
        amount: amountBN.toFixed(precision.quantity, 1),
        unitView: symbol.split('_')[0]?.toUpperCase(),
      };
  }

  /** 固定以标的币种展示数量（不受下单单位切换影响） */
  @action.bound
  getUnitAmountBaseCoin(
    amount: string,
    symbol: string,
    price: string,
    underlyingType?: UnderLyingEnum,
  ): { amount: string; unitView: string } {
    const config = this.rootStore.ticker.getSymbolConfig(symbol);
    const precision = this.rootStore.ticker.getPrecision(symbol);

    if (!(config && precision) || !amount || new BigNumber(amount).isNaN()) {
      return {
        amount: '--',
        unitView: '',
      };
    }

    const isUBased =
      (underlyingType && underlyingType === UnderLyingEnum.U_BASED) ||
      (!underlyingType && this.rootStore.underlying.isUBased);

    // U 本位：张数 × 面值，不依赖价格；币本位才需要价格换算
    if (!isUBased && new BigNumber(price || 0).lte(0)) {
      return {
        amount: '--',
        unitView: '',
      };
    }

    const amountBN = isUBased
      ? new BigNumber(amount).times(config.contractSize)
      : new BigNumber(amount).times(config.contractSize).div(price);

    return amountBN.isNaN()
      ? {
        amount: '--',
        unitView: '',
      }
      : {
        amount: amountBN.toFixed(precision.quantity, 1),
        unitView: symbol.split('_')[0]?.toUpperCase(),
      };
  }

  /** 张数 → 成交额（U 本位：张 × 面值 × 价；币本位：张 × 面值 USD） */
  @action.bound
  getDealAmountFromSheet(
    sheetQty: string,
    symbol: string,
    price: string,
    underlyingType?: UnderLyingEnum,
  ): string {
    const config = this.rootStore.ticker.getSymbolConfig(symbol);
    const precision = this.rootStore.ticker.getPrecision(symbol);

    if (!(config && precision)) {
      return '--';
    }

    const isUBased =
      (underlyingType && underlyingType === UnderLyingEnum.U_BASED) ||
      (!underlyingType && this.rootStore.underlying.isUBased);

    const dealAmountBN = isUBased
      ? new BigNumber(sheetQty || 0).times(config.contractSize).times(price || 0)
      : new BigNumber(sheetQty || 0).times(config.contractSize);

    if (isUBased && new BigNumber(price || 0).lte(0)) {
      return '--';
    }

    return dealAmountBN.isNaN()
      ? '--'
      : dealAmountBN.toFixed(precision.quoteCoinDisplay, 1);
  }

  // 市场列表顺序与 /market/config/tradePairs/getList（SYMBOL_CONFIG_LIST）一致：用 ticker.allSymbolConfigList
  //（在 setUBased / setCoin 时由两次 getList 合并，并按接口 sort/sequence 等字段排序）
  @computed get marketList() {
    const tickerList = this.rootStore.ticker.tickerList;
    const { allSymbolConfigList, uBasedSymbolConfigList, coinBasedSymbolConfigList } = this.rootStore.ticker;
    const allConfigs =
      allSymbolConfigList.length > 0
        ? allSymbolConfigList
        : [...uBasedSymbolConfigList, ...coinBasedSymbolConfigList];

    const tickerMap = new Map(tickerList.map((item: TickerInfo) => [item.s, item]));
    const configSymbolSet = new Set(allConfigs.map(c => c.symbol));

    const fromConfig = allConfigs.map((config: SymbolConfig) => {
      const obj =
        tickerMap.get(config.symbol) ||
        ({
          s: config.symbol,
          c: '0',
          r: '0',
        } as TickerInfo);

      return {
        ...obj,
        labels: config.labels,
        type: 1,
        logo: this.getSymbolCoinName(config.symbol),
        underlyingType: config.underlyingType,
      };
    });

    const orphans = tickerList
      .filter((item: TickerInfo) => !configSymbolSet.has(item.s))
      .sort((a, b) => (a.s < b.s ? -1 : a.s > b.s ? 1 : 0))
      .map((item: TickerInfo) => {
        const row = item as TickerInfo & { labels?: string[]; underlyingType?: UnderLyingEnum };
        return {
          ...item,
          labels: row.labels,
          type: 1,
          logo: this.getSymbolCoinName(item.s),
          underlyingType: row.underlyingType,
        };
      });

    return [...fromConfig, ...orphans];
  }

  // 热门榜
  @computed get marketHotList() {
    const tickerList = this.rootStore.ticker.tickerList || [];
    const uBasedSymbolConfigList = this.rootStore.ticker.uBasedSymbolConfigList || [];
    const coinBasedSymbolConfigList = this.rootStore.ticker.coinBasedSymbolConfigList || [];
    return [...uBasedSymbolConfigList, ...coinBasedSymbolConfigList]
      .filter(i => Array.isArray(i.labels) && i.labels.includes('HOT'))
      .map((item: SymbolConfig) => {
        const obj = tickerList.find((o: TickerInfo) => o.s === item.symbol) || {
          c: '0',
          r: '0',
          s: item.symbol,
          logo: this.getSymbolCoinName(item.symbol), // logo图片
        };

        return {
          ...obj,
          labels: item.labels || [],
          logo: this.getSymbolCoinName(item.symbol), // logo图片
          underlyingType: item.underlyingType,
        };
      });
  }

  // 涨幅
  @computed get marketUpList() {
    const tickerList = this.rootStore.ticker.tickerList;
    // console.log(tickerList);
    // const uBasedSymbolConfigList = this.rootStore.ticker.uBasedSymbolConfigList;
    // const coinBasedSymbolConfigList = this.rootStore.ticker.coinBasedSymbolConfigList;

    // const res = [...uBasedSymbolConfigList, ...coinBasedSymbolConfigList].map((item: SymbolConfig) => {
    //   const obj = tickerList.find((o: TickerInfo) => o.s === item.symbol) || {
    //     c: '0',
    //     r: '0',
    //     s: item.symbol,
    //     logo: this.getSymbolCoinName(item.symbol), // logo图片
    //   };

    //   return {
    //     logo: this.getSymbolCoinName(item.symbol), // logo图片
    //     ...obj,
    //     labels: item.labels,
    //     underlyingType: item.underlyingType,
    //   };
    // });

    return tickerList
      .map((item: any) => ({
        ...item, // 复制对象
        logo: this.getSymbolCoinName(item.s), // 计算 logo
      }))
      .sort((a, b) => Number(b.r) - Number(a.r));
  }

  // 涨跌
  @computed get marketDownList() {
    const tickerList = this.rootStore.ticker.tickerList;
    // const uBasedSymbolConfigList = this.rootStore.ticker.uBasedSymbolConfigList;
    // const coinBasedSymbolConfigList = this.rootStore.ticker.coinBasedSymbolConfigList;

    // const res = [...uBasedSymbolConfigList, ...coinBasedSymbolConfigList].map((item: SymbolConfig) => {
    //   const obj = tickerList.find((o: TickerInfo) => o.s === item.symbol) || {
    //     c: '0',
    //     r: '0',
    //     s: item.symbol,
    //     logo: this.getSymbolCoinName(item.symbol), // logo图片
    //   };

    //   return {
    //     ...obj,
    //     labels: item.labels,
    //     underlyingType: item.underlyingType,
    //     logo: this.getSymbolCoinName(item.symbol), // logo图片
    //   };
    // });

    return tickerList
      .map((item: any) => ({
        ...item, // 复制对象
        logo: this.getSymbolCoinName(item.s), // 计算 logo
      }))
      .sort((a, b) => Number(a.r) - Number(b.r));
  }

  // 交易榜
  @computed get marketVolList() {
    const tickerList = this.rootStore.ticker.tickerList;
    // const uBasedSymbolConfigList = this.rootStore.ticker.uBasedSymbolConfigList;
    // const coinBasedSymbolConfigList = this.rootStore.ticker.coinBasedSymbolConfigList;

    // const res = [...uBasedSymbolConfigList, ...coinBasedSymbolConfigList].map((item: SymbolConfig) => {
    //   const obj = tickerList.find((o: TickerInfo) => o.s === item.symbol) || {
    //     c: '0',
    //     r: '0',
    //     v: '0',
    //     logo: this.getSymbolCoinName(item.symbol), // logo图片
    //     s: item.symbol,
    //   };

    //   return {
    //     ...obj,
    //     logo: this.getSymbolCoinName(item.symbol), // logo图片
    //     labels: item.labels,
    //     underlyingType: item.underlyingType,
    //   };
    // });

    return tickerList
      .map((item: any) => ({
        ...item, // 复制对象
        logo: this.getSymbolCoinName(item.s), // 计算 logo
      }))
      .sort((a, b) => Number(b.v) - Number(a.v));
  }

  @computed get coinBalanceList(): CoinBalance[] {
    const balanceList = this.rootStore.asset.balanceList;
    const positionListWithValue = this.positionListWithValue;
    const generateBalanceData = this.generateBalanceData;
    const isUBased = this.rootStore.underlying.isUBased;

    if (isUBased) {
      return [];
    }

    return balanceList.map((item: BalanceDetail): CoinBalance => generateBalanceData(item, positionListWithValue));
  }

  /**
   * dex（共享保证金） = 钱包余额 + 体验金余额 - ∑逐仓仓位保证金 - ∑全仓维持保证金 - ∑委托保证金 + ∑除本仓位其他全仓仓位未实现盈亏
   * 合约 account/detail 的 walletBalance 不含体验金，计算时需加上 bonusBalance（兼容 bonus）
   * @param symbol
   * @param positionSide
   * @param positionList 持仓列表（用于计算全仓维持保证金和浮动盈亏）
   * @param isCreateOrder 是否下单场景
   * @param useCopyBalance 是否使用跟单账户余额（跟单用户计算跟单强平价时使用）
   */
  @action.bound
  getDex(symbol: string, positionSide: PositionSide, positionList?: PositionItem[], isCreateOrder?: boolean, useCopyBalance?: boolean) {
    // 如果传入了 positionList 参数，使用传入的；否则使用 store 中的
    const list = positionList || this.rootStore.position.positionList;
    const getPositionBracket = this.rootStore.ticker.getPositionBracket;
    const getSymbolUnderlyingType = this.rootStore.ticker.getSymbolUnderlyingType;
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const getFloatProfit = this.getFloatProfit;
    const getBalanceByCoin = this.rootStore.asset.getBalanceByCoin;
    const isUBased = this.rootStore.underlying.isUBased;
    const balance = this.rootStore.asset.balance;
    const balanceList = this.rootStore.asset.balanceList;
    const leaderApplyState = this.leaderApplyState;
    const coin = symbol?.split('_')[0];
    const balanceCoin = balance.coin;
    // 显式读取 balanceList 以确保 MobX 追踪（即使 find 可能返回 undefined，也要读取数组本身）
    void balanceList.length;
    // 跟单余额：必须按「当前 symbol 的 coin」取，避免多条 COPY 时取到错误币种
    const copyBalance = balanceList.find((item: any) => item.balanceType == 'COPY' && item.coin === coin);
    // 若跟单接口只返回一条总 COPY（无 coin 或 coin 为空），则回退为仅按 balanceType 取
    const copyBalanceFallback = !copyBalance
      ? balanceList.find((item: any) => item.balanceType == 'COPY')
      : null;
    const copyBalanceToUse = copyBalance ?? copyBalanceFallback;
    if (copyBalanceToUse) {
      void copyBalanceToUse.availableBalance;
      void copyBalanceToUse.walletBalance;
      void copyBalanceToUse.isolatedMargin;
      void copyBalanceToUse.openOrderMarginFrozen;
    }

    // 当 useCopyBalance=true 时，使用跟单账户余额和逐仓保证金；否则使用主账户
    let walletBalance = '0';
    let bonusBalance = '0';
    let isolatedMargin = '0';
    let openOrderMarginFrozen = '0';

    if (useCopyBalance) {
      // 跟单账户：从 copyBalance（当前币种）获取余额和逐仓保证金
      walletBalance = copyBalanceToUse?.walletBalance || '0';
      bonusBalance = copyBalanceToUse?.bonusBalance ?? copyBalanceToUse?.bonus ?? '0';
      isolatedMargin = copyBalanceToUse?.isolatedMargin || '0';
      openOrderMarginFrozen = copyBalanceToUse?.openOrderMarginFrozen || '0';
      void copyBalanceToUse?.bonusBalance;
      void copyBalanceToUse?.bonus;
    } else {
      // 主账户：必须排除 COPY，只取 MAIN，否则 getBalanceByCoin(coin) 可能取到跟单那条导致取错余额
      const mainBalanceByCoin = balanceList.find((item: any) => item.coin === coin && (item.balanceType || 'CONTRACT') === 'CONTRACT');
      const balanceData =
        isUBased || coin === balanceCoin
          ? balance
          : (mainBalanceByCoin ?? getBalanceByCoin(coin) ?? {});
      walletBalance = balanceData.walletBalance || '0';
      // account/detail：合约强平共享保证金需计入体验金余额
      bonusBalance = balanceData.bonusBalance ?? balanceData.bonus ?? '0';
      isolatedMargin = balanceData.isolatedMargin || '0';
      openOrderMarginFrozen = balanceData.openOrderMarginFrozen || '0';
      void balanceData.bonusBalance;
      void balanceData.bonus;
      void balanceData.walletBalance;
    }

    let crossMaintMargin = new BigNumber(0);
    let crossedFloatProfit = new BigNumber(0);
    let unsettledProfit = new BigNumber(0);

    // 只遍历全仓仓位：crossMaintMargin、unsettledProfit、crossedFloatProfit 均只来自「全仓」列表
    list.forEach((p: PositionItem, index: number) => {
      const { symbol: s, positionSide: ps, positionType: pt, entryPrice, positionSize } = p;

      if (pt === 'CROSSED' && new BigNumber(positionSize).gt(0)) {
        const currentBracket = getPositionBracket(p);
        // alert(JSON.stringify(currentBracket));
        const { maintMarginRate } = currentBracket as LeverageBracketItem;
        const underlyingType = getSymbolUnderlyingType(s);
        const { contractSize } = getSymbolConfig(s);

        // 修复：确保关键配置存在
        if (!maintMarginRate || !contractSize) {
          return;
        }

        if (underlyingType === UnderLyingEnum.U_BASED) {
          // 只累加「除本仓位外」的全仓浮动盈亏：本方向当前仓位的盈亏在强平价公式里用 positionValue 体现；
          // 同交易对反向仓位须计入，否则会漏算双向持仓（对冲）时另一侧的未实现盈亏（与币本位分支 ps !== positionSide 一致）
          if (!(s === symbol && ps === positionSide)) {
            const fp = getFloatProfit(p);
            crossedFloatProfit = crossedFloatProfit.plus(fp || 0);
          }
          crossMaintMargin = crossMaintMargin.plus(
            new BigNumber(getValue(entryPrice, positionSize, contractSize)).times(maintMarginRate),
          );
          unsettledProfit = unsettledProfit.plus(p.unsettledProfit || 0);
        } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
          // 币本位的计算只关心当前币种
          if (s === symbol) {
            if (ps !== positionSide) {
              const fp = getFloatProfit(p);
              crossedFloatProfit = crossedFloatProfit.plus(fp || 0);
            }

            crossMaintMargin = crossMaintMargin.plus(
              new BigNumber(getCoinValue(entryPrice, positionSize, contractSize)).times(maintMarginRate),
            );
          }
        }
      }
    });

    // 全仓 dex计算 dex=余额-余额上的逐仓保证金-全仓维持保证金-所有持仓手续费+其他非本币种全仓仓位未实现盈亏

    // 余额选择逻辑：
    // 1. useCopyBalance=true（跟单用户计算跟单强平价）：强制使用跟单账户余额
    // 2. isCreateOrder=true（下单场景）：使用主账户余额
    // 3. 其他情况：使用主账户余额
    // 口径：dex = (walletBalance + bonusBalance) - 逐仓保证金 - 全仓维持保证金 - 手续费 + 其他全仓未实现盈亏
    // 合约 account/detail 的 walletBalance 不含体验金，需加上 bonusBalance（兼容旧字段 bonus）
    const walletWithBonus = new BigNumber(walletBalance).plus(bonusBalance || 0).valueOf();
    let balanceValue: string;
    if (useCopyBalance) {
      const copyWallet = copyBalanceToUse?.walletBalance ?? walletBalance;
      const copyBonus = copyBalanceToUse?.bonusBalance ?? copyBalanceToUse?.bonus ?? bonusBalance;
      balanceValue = new BigNumber(copyWallet || 0).plus(copyBonus || 0).valueOf();
    } else if (isCreateOrder) {
      balanceValue = walletWithBonus;
    } else {
      balanceValue = walletWithBonus;
    }
    const dexResult = new BigNumber(balanceValue)
      .minus(isolatedMargin)
      .minus(crossMaintMargin)
      .minus(unsettledProfit) // 手续费（仅全仓）
      // .minus(openOrderMarginFrozen)
      .plus(crossedFloatProfit)
      .valueOf();

    // 全仓强平价差异排查：与 App 对比时可在控制台查看各项（无逐仓时若仍有差异，重点对比 balanceValue、crossMaintMargin、unsettledProfit、crossedFloatProfit）
    // if (process.env.NODE_ENV !== 'production' && typeof symbol === 'string') {
    //   console.log('[getDex]', symbol, {
    //     balanceValue,
    //     isolatedMargin,
    //     crossMaintMargin: crossMaintMargin.valueOf(),
    //     unsettledProfit: unsettledProfit.valueOf(),
    //     crossedFloatProfit: crossedFloatProfit.valueOf(),
    //     dexResult,
    //   });
    // }

    return dexResult;
  }

  /**
   * 按 symbol 汇总全仓开仓名义价值，与 App symbolCrossTemp1/symbolCrossTemp2 一致
   * temp1 = ∑(开仓价 * 方向 * 数量 * 面值)，temp2 = ∑(方向 * 数量 * 面值)，多仓方向=1，空仓=-1
   */
  @action.bound
  getSymbolCrossTemp1Temp2(
    symbol: string,
    positionList: PositionItem[],
    getSymbolConfig: (s: string) => { contractSize?: string | number },
  ): { symbolCrossTemp1: BigNumber; symbolCrossTemp2: BigNumber } {
    let symbolCrossTemp1 = new BigNumber(0);
    let symbolCrossTemp2 = new BigNumber(0);
    const config = getSymbolConfig(symbol);
    const contractSize = config?.contractSize;
    if (!contractSize) {
      return { symbolCrossTemp1, symbolCrossTemp2 };
    }
    positionList.forEach((p: PositionItem) => {
      const { symbol: s, positionType: pt, entryPrice, positionSize, positionSide: ps } = p;
      if (s !== symbol || pt !== 'CROSSED' || !new BigNumber(positionSize).gt(0)) {
        return;
      }
      const side = ps === 'LONG' ? 1 : -1;
      const term1 = new BigNumber(entryPrice).times(side).times(positionSize).times(contractSize);
      const term2 = new BigNumber(side).times(positionSize).times(contractSize);
      symbolCrossTemp1 = symbolCrossTemp1.plus(term1);
      symbolCrossTemp2 = symbolCrossTemp2.plus(term2);
    });
    return { symbolCrossTemp1, symbolCrossTemp2 };
  }

  // 持仓中的强平价计算（与 App 一致：全仓按 symbol 汇总 temp1/temp2 进行单仓展示，同一 symbol 下强平价相同）
  // usePerPositionForCross=true 时全仓改为按单仓位计算（用于交易员当前带单列表）
  @action.bound
  getLiquidationPrice(
    position: PositionItem,
    positionList: PositionItem[],
    useCopyBalance?: boolean,
    usePerPositionForCross?: boolean,
  ) {
    const { symbol, positionSize, positionSide, entryPrice, isolatedMargin, positionType, unsettledProfit } = position;
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const getPositionBracket = this.rootStore.ticker.getPositionBracket;
    const getSymbolUnderlyingType = this.rootStore.ticker.getSymbolUnderlyingType;
    const currentSymbolConfig = getSymbolConfig(symbol);
    const { contractSize } = currentSymbolConfig;
    // console.log(`======contractSize-${contractSize}======`);
    if (contractSize) {
      const underlyingType = getSymbolUnderlyingType(symbol);
      const currentBracket = getPositionBracket(position);
      const { maintMarginRate } = currentBracket as LeverageBracketItem;
      // console.log(`======currentBracket-${JSON.stringify(maintMarginRate)}======`);

      // 如果 maintMarginRate 不存在，无法计算强平价
      if (!maintMarginRate) {
        return '--';
      }

      let result = new BigNumber('');

      if (underlyingType === UnderLyingEnum.U_BASED) {
        if (positionType === 'CROSSED') {
          const dex = this.getDex(symbol, positionSide, positionList, false, useCopyBalance);
          const positionValue = getValue(entryPrice, positionSize, contractSize);
          const denominator = new BigNumber(positionSize).times(contractSize);
          if (positionSide === 'LONG') {
            result = new BigNumber(positionValue).minus(dex).div(denominator);
          } else if (positionSide === 'SHORT') {
            result = new BigNumber(positionValue).plus(dex).div(denominator);
          }

          // if (process.env.NODE_ENV !== 'production' && typeof symbol === 'string') {
          //   console.log('[getLiquidationPrice 全仓]', symbol, positionSide, {
          //     usePerPositionForCross,
          //     dex,
          //     resultRaw: result.valueOf(),
          //   });
          // }
        } else if (positionType === 'ISOLATED') {
          // 逐仓不参与 getDex，强平价公式里单独用本仓位的 unsettledProfit（多仓+、空仓-）
          // 带单/跟单 positionLike 写死 unsettledProfit: '0' 时，仅此处会产生与真实持仓的强平价差
          if (positionSide === 'LONG') {
            // 多仓[UP]：强平价格 = (维持保证金-仓位保证金+仓位手续费+开仓价格*数量*面值)/(数量*面值)
            // 维持保证金 = 数量*面值*开仓均价*维持保证金率
            result = new BigNumber(getValue(entryPrice, positionSize, contractSize))
              .plus(new BigNumber(getValue(entryPrice, positionSize, contractSize)).times(maintMarginRate))
              .minus(isolatedMargin)
              .plus(unsettledProfit)
              .div(new BigNumber(positionSize).times(contractSize));
          } else if (positionSide === 'SHORT') {
            // 空仓[DOWN]：强平价格 = (开仓价格*数量*面值-维持保证金+仓位保证金-仓位手续费)/(数量*面值)
            result = new BigNumber(getValue(entryPrice, positionSize, contractSize))
              .minus(new BigNumber(getValue(entryPrice, positionSize, contractSize)).times(maintMarginRate))
              .plus(isolatedMargin)
              .minus(unsettledProfit)
              .div(new BigNumber(positionSize).times(contractSize));
          }
        }
      } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
        if (positionType === 'CROSSED') {
          const dex = this.getDex(symbol, positionSide, positionList, false, useCopyBalance);
          const denominator = positionSide === 'LONG'
            ? new BigNumber(positionSize).times(contractSize).plus(new BigNumber(entryPrice).times(dex))
            : new BigNumber(positionSize).times(contractSize).minus(new BigNumber(entryPrice).times(dex));

          // 检查分母是否过小，避免除以零或得到异常结果
          if (denominator.abs().lt(0.0001)) {
            // 合仓模式下，分母过小时返回无效值
            result = new BigNumber(NaN);
          } else {
            if (positionSide === 'LONG') {
              // 多仓强平价格 = 数量 * 面值 * 开仓均价 / (数量 * 面值 + 开仓均价 * dex)
              result = new BigNumber(positionSize)
                .times(contractSize)
                .times(entryPrice)
                .div(denominator);
            } else if (positionSide === 'SHORT') {
              // 空仓强平价格 = 数量 * 面值 * 开仓均价 / (数量 * 面值 - 开仓均价 * dex)
              result = new BigNumber(positionSize)
                .times(contractSize)
                .times(entryPrice)
                .div(denominator);
            }
          }
        } else if (positionType === 'ISOLATED') {
          if (positionSide === 'LONG') {
            // 多仓[UP]：强平价格 = 开仓均价 * 数量 * 面值 / (数量 * 面值 + 开仓均价 * (仓位保证金 - 维持保证金))
            result = new BigNumber(entryPrice)
              .times(positionSize)
              .times(contractSize)
              .div(
                new BigNumber(positionSize).times(contractSize).plus(
                  new BigNumber(isolatedMargin)
                    .minus(
                      new BigNumber(positionSize)
                        .times(contractSize)
                        .div(entryPrice)
                        .times(maintMarginRate),
                    )
                    .times(entryPrice),
                ),
              );
          } else if (positionSide === 'SHORT') {
            // 空仓[DOWN]：强平价格 = 开仓均价 * 数量 * 面值 / (数量 * 面值 + 开仓均价 * (维持保证金 - 仓位保证金))
            result = new BigNumber(entryPrice)
              .times(positionSize)
              .times(contractSize)
              .div(
                new BigNumber(positionSize).times(contractSize).plus(
                  new BigNumber(positionSize)
                    .times(contractSize)
                    .div(entryPrice)
                    .times(maintMarginRate)
                    .minus(isolatedMargin)
                    .times(entryPrice),
                ),
              );
          }
        }
      }
      // console.log(`======result-${result.valueOf()}======symbol-${symbol}======`);
      return result.isNaN() ? '--' : result.valueOf();
    }

    return '--';
  }
  // 下确认弹框预估强平价格计算
  @action.bound
  getEstimatedForcePrice(position: any) {
    const { symbol, positionSide, estimatedQuantity, quantity, price: entryPrice, isolatedMargin, positionType, positionMode, unsettledProfit, leverage, amountUnit } = position;
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const getPositionBracket = this.rootStore.ticker.getPositionBracket;
    const getSymbolUnderlyingType = this.rootStore.ticker.getSymbolUnderlyingType;
    const currentSymbolConfig = getSymbolConfig(symbol);
    const { contractSize } = currentSymbolConfig;
    if (contractSize) {
      const underlyingType = getSymbolUnderlyingType(symbol);
      const currentBracket = getPositionBracket(position);
      const { maintMarginRate } = currentBracket as LeverageBracketItem;
      // 优先使用 estimatedQuantity，如果没有则使用 quantity 计算
      if (!quantity) {
        return '--';
      }

      // 根据数量单位将 qty 转换为张数（positionSize）
      // 参考 getSheetAmount 的逻辑


      const positionSize = quantity;
      let result = new BigNumber('');

      // 计算预估保证金（如果未传入）
      let estimatedIsolatedMargin = isolatedMargin;
      if ((!estimatedIsolatedMargin || estimatedIsolatedMargin === '0') && leverage && entryPrice) {
        if (underlyingType === UnderLyingEnum.U_BASED) {
          // U本位：预估保证金 = 开仓价 * 数量 * 面值 / 杠杆
          // 注意：这里必须乘以开仓价，否则会导致预估强平价明显偏高（接近或高于开仓价）
          estimatedIsolatedMargin = new BigNumber(positionSize)
            .times(contractSize)
            .times(entryPrice)
            .div(leverage)
            .toString();
        } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
          // 币本位：预估保证金 = 数量 * 面值 / 开仓均价 / 杠杆
          estimatedIsolatedMargin = new BigNumber(positionSize).times(contractSize).div(entryPrice).div(leverage).toString();
        }
      }
      if (underlyingType === UnderLyingEnum.U_BASED) {
        if (positionType === 'CROSSED') {
          const isCreateOrder = true;
          const dex = this.getDex(symbol, positionSide, undefined, isCreateOrder);
          const positionValue = getValue(entryPrice, positionSize, contractSize);
          const denominator = new BigNumber(positionSize).times(contractSize);

          // console.log('========== 强平价格计算 ==========');
          // console.log('symbol:', symbol);
          // console.log('positionSide:', positionSide);
          // console.log('entryPrice:', entryPrice);
          // console.log('positionSize:', positionSize);
          // console.log('contractSize:', contractSize);
          // console.log('positionValue (开仓价*数量*面值):', positionValue);
          // console.log('dex:', dex);
          // console.log('denominator (数量*面值):', denominator.toString());

          if (positionSide === 'LONG') {
            // 多仓强平价格 = (开仓均价 * 数量 * 面值 - dex) / (数量 * 面值)
            result = new BigNumber(positionValue)
              .minus(dex)
              .div(denominator);
            // 多仓强平价格 = (开仓均价 * 数量 * 面值 - dex) / (数量 * 面值)
          } else if (positionSide === 'SHORT') {
            // 空仓强平价格 = (开仓均价 * 数量 * 面值 + dex) / (数量 * 面值)
            result = new BigNumber(positionValue)
              .plus(dex)
              .div(denominator);
            // console.log('空仓强平价格 = (', positionValue, '+', dex, ') /', denominator.toString(), '=', result.toString());
          }
        } else if (positionType === 'ISOLATED') {
          // 多仓[UP]：强平价格 = (维持保证金-仓位保证金+仓位手续费+开仓价格*数量*面值)/(数量*面值)
          // 空仓[DOWN]：强平价格 = (开仓价格*数量*面值-维持保证金+仓位保证金-仓位手续费)/(数量*面值)
          if (positionSide === 'LONG') {
            result = new BigNumber(getValue(entryPrice, positionSize.toString(), contractSize))
              .plus(new BigNumber(getValue(entryPrice, positionSize.toString(), contractSize)).times(maintMarginRate))
              .minus(estimatedIsolatedMargin || 0)
              .plus(unsettledProfit || 0)
              .div(new BigNumber(positionSize).times(contractSize));
          } else if (positionSide === 'SHORT') {
            result = new BigNumber(getValue(entryPrice, positionSize.toString(), contractSize))
              .minus(new BigNumber(getValue(entryPrice, positionSize.toString(), contractSize)).times(maintMarginRate))
              .plus(estimatedIsolatedMargin || 0)
              .minus(unsettledProfit || 0)
              .div(new BigNumber(positionSize).times(contractSize));
          }
        }
      } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
        if (positionType === 'CROSSED') {
          result = new BigNumber(positionSize)
            .times(contractSize)
            .times(entryPrice)
            .div(new BigNumber(positionSize).times(contractSize).plus(new BigNumber(entryPrice).times(this.getDex(symbol, positionSide))));
        } else if (positionType === 'ISOLATED') {
          // 多仓[UP]：强平价格 = 开仓均价 * 数量 * 面值 / (数量 * 面值 + 开仓均价 * (仓位保证金 - 维持保证金))
          // 空仓[DOWN]：强平价格 = 开仓均价 * 数量 * 面值 / (数量 * 面值 + 开仓均价 * (维持保证金 - 仓位保证金))
          if (positionSide === 'LONG') {
            result = new BigNumber(entryPrice)
              .times(positionSize)
              .times(contractSize)
              .div(
                new BigNumber(positionSize).times(contractSize).plus(
                  new BigNumber(estimatedIsolatedMargin || 0)
                    .minus(
                      new BigNumber(positionSize)
                        .times(contractSize)
                        .div(entryPrice)
                        .times(maintMarginRate),
                    )
                    .times(entryPrice),
                ),
              );
          } else if (positionSide === 'SHORT') {
            result = new BigNumber(entryPrice)
              .times(positionSize)
              .times(contractSize)
              .div(
                new BigNumber(positionSize).times(contractSize).plus(
                  new BigNumber(positionSize)
                    .times(contractSize)
                    .div(entryPrice)
                    .times(maintMarginRate)
                    .minus(estimatedIsolatedMargin || 0)
                    .times(entryPrice),
                ),
              );
          }
        }
      }
      return result.isNaN() ? '--' : result.valueOf();
    }

    return '--';
  }
  getSymbolName(symbol?: string) {
    // const lang = this.rootStore.account.lang;
    // const symbolConfigList = this.rootStore.ticker.allSymbolConfigList;
    //
    // const config = symbolConfigList.find((item: SymbolConfig) => item.symbol === symbol);
    //
    // if (config) {
    //   return lang === 'zh' ? config.cnName : config.enName;
    // }
    //
    // return '';

    return `${symbol?.replace(/_/g, '')?.toUpperCase()} ${i18n.t('placeholder.permanent')}`;
  }

  @action.bound
  getSymbolCoinName(symbol?: string) {
    // const lang = this.rootStore.account.lang;
    // const symbolConfigList = this.rootStore.ticker.allSymbolConfigList;
    //
    // const config = symbolConfigList.find((item: SymbolConfig) => item.symbol === symbol);
    //
    // if (config) {
    //   return lang === 'zh' ? config.cnName : config.enName;
    // }
    //
    // return '';
    return symbol?.split('_')[0].toUpperCase();
  }

  @action.bound
  getSymbolNamePart(symbol?: string) {
    // const lang = this.rootStore.account.lang;
    // const symbolConfigList = this.rootStore.ticker.allSymbolConfigList;
    //
    // const config = symbolConfigList.find((item: SymbolConfig) => item.symbol === symbol);
    //
    // if (config) {
    //   return lang === 'zh' ? config.cnName : config.enName;
    // }
    //
    // return '';
    if (!symbol) {
      return {
        symbol: '',
        coin: '',
        text: ``,
      };
    }
    const parts = symbol.split('_');
    return {
      symbol: parts[0]?.toUpperCase() || '',
      coin: parts[1]?.toUpperCase() || '',
      text: `${i18n.t('placeholder.permanent')}`,
    };
  }

  @action.bound
  getSymbolNameSingle(symbol?: string) {
    // const lang = this.rootStore.account.lang;
    // const symbolConfigList = this.rootStore.ticker.allSymbolConfigList;
    //
    // const config = symbolConfigList.find((item: SymbolConfig) => item.symbol === symbol);
    //
    // if (config) {
    //   return lang === 'zh' ? config.cnName : config.enName;
    // }
    //
    // return '';

    return `${symbol?.replace(/_/g, '/')?.toUpperCase()}`;
  }

  @action.bound
  getCrossMaintainMargin(position: PositionItem) {
    const { symbol, positionSize, entryPrice } = position;
    const getPositionBracket = this.rootStore.ticker.getPositionBracket;
    const getSymbolUnderlyingType = this.rootStore.ticker.getSymbolUnderlyingType;
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;

    let crossMaintMargin = new BigNumber(0);

    const currentBracket = getPositionBracket(position);
    const { maintMarginRate } = currentBracket as LeverageBracketItem;
    const underlyingType = getSymbolUnderlyingType(symbol);
    const { contractSize } = getSymbolConfig(symbol);

    if (underlyingType === UnderLyingEnum.U_BASED) {
      crossMaintMargin = new BigNumber(getValue(entryPrice, positionSize, contractSize)).times(maintMarginRate);
    } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
      crossMaintMargin = new BigNumber(getCoinValue(entryPrice, positionSize, contractSize)).times(maintMarginRate);
    }

    return crossMaintMargin.isNaN() ? '0' : crossMaintMargin.valueOf();
  }

  @action.bound
  setPositionConf(conf: PositionConf) {
    this.positionConf = conf;
  }

  @action.bound
  async fetchPositionConf(symbol: string | undefined) {
    if (!symbol) return;

    const res = await Axios.get(API.POSITION_MODE_CONF, {
      params: {
        symbol,
      },
    });

    const { initLeverage, initPositionType } = this.rootStore.ticker.getSymbolConfig(symbol);
    const conf1 = Array.isArray(res) && res.length > 0 ? res[0] : {};
    const conf2 = {
      leverage: conf1.leverage || initLeverage,
      positionType: conf1?.positionType || initPositionType,
      positionModel: conf1?.positionModel || 'AGGREGATION' as PositionMode,
    };

    this.setPositionConf({
      ...conf1,
      ...conf2,
    });
  }

  @action.bound
  setLeaderApplyState(data: any) {
    this.leaderApplyState = data;
  }

  @action.bound
  async fetchLeaderApplyState() {
    const res = await Axios.get(API.APPLY_COPY_STATE, {});

    this.setLeaderApplyState(res);
  }

  @computed get currentLeaderOrderListWithConfig() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const getPrecision = this.rootStore.ticker.getPrecision;
    const symbolMarkPriceMap = this.rootStore.ticker.markPriceMap;
    const orderList = cloneDeep(this.leaderHomeOrderList);
    const getFloatProfit = this.getFloatProfit;
    const getLiquidationPrice = this.getLiquidationPrice;
    // 交易员的带单即为持仓：带单列表与持仓列表是同一批仓位，故用 positionList 参与 getDex 与强平价计算
    // 强平价依赖用户真实持仓与余额（getDex），显式读取以建立 MobX 依赖，持仓/余额变化时本 computed 会重算、视图会更新
    const positionList = this.rootStore.position.positionList;
    void positionList.length;
    void this.rootStore.asset.balance.walletBalance;

    // 从持仓列表取未结算手续费，用于逐仓强平价公式，避免写死 0 导致与真实强平价偏差
    const getLeaderUnsettledProfit = (item: LeaderOrderItem, list: PositionItem[]) => {
      if (!item || !list?.length) return '0';
      const matchById = list.find((p: PositionItem) => String(p.positionId) === String(item.positionId));
      if (matchById?.unsettledProfit !== undefined && matchById.unsettledProfit !== '') {
        return matchById.unsettledProfit;
      }
      const matchByKey = list.find(
        (p: PositionItem) =>
          p.symbol === item.symbolName &&
          p.positionSide === item.positionSide &&
          p.positionType === item.positionType,
      );
      return matchByKey?.unsettledProfit ?? '0';
    };

    const calculatedList = orderList.map((item: LeaderOrderItem) => {
      // 添加本地计算值
      item.floatProfit = '0.00';
      item.floatProfitRate = '0.00';
      item.floatProfitForMarkPrice = '0.00';
      item.liquidationPrice = '--';

      const { symbolName: symbol, openMargin: isolatedMargin, positionSide } = item;

      // 带单若未带仓位模式，从持仓回填，保证逐仓分仓不强行对齐强平价
      if (!item.positionModel) {
        const matched = positionList.find(
          (p: PositionItem) =>
            String(p.positionId) === String(item.positionId) ||
            (p.symbol === item.symbolName &&
              p.positionSide === item.positionSide &&
              p.positionType === item.positionType),
        );
        if (matched?.positionModel) item.positionModel = matched.positionModel;
      }

      // 预估强平价：按单仓位计算（usePerPositionForCross=true），用带单数据构造等价 PositionItem，传入持仓的未结算手续费以修正逐仓强平价
      const positionLike = {
        symbol: item.symbolName,
        entryPrice: item.openPrice,
        isolatedMargin: item.openMargin,
        positionSize: item.positionSize,
        positionSide: item.positionSide,
        positionType: item.positionType,
        positionModel: item.positionModel,
        unsettledProfit: getLeaderUnsettledProfit(item, positionList),
        leverage: item.openLeverage,
      } as PositionItem;
      item.liquidationPrice = getLiquidationPrice(positionLike, positionList, false, true);
      const symbolConfig = getSymbolConfig(symbol);
      const markPrice = symbolMarkPriceMap[symbol];

      item.markPrice = markPrice;

      if (symbolConfig) {
        const { contractSize, pricePrecision, quoteCoinDisplayPrecision } = symbolConfig;
        const { quantity: quantityPrecision } = getPrecision(symbol);

        // 添加其他配置信息
        item.contractSize = contractSize;
        item.quantityPrecision = quantityPrecision; // 数量精度
        item.pricePrecision = pricePrecision; // 价格精度
        item.quoteCoinDisplayPrecision = quoteCoinDisplayPrecision; // 计价币种精度

        // 浮动盈亏
        // U本位做多：数量(张) * 合约面值(张) * (标记价格-开仓均价)；
        // U本位做空：数量(张) * 合约面值(张) * (开仓均价-标记价格)；
        // 币本位做多：数量(张) * 合约面值(张) * (1 / 开仓均价 - 1 / 标记价格)
        // 币本位做空：数量(张) * 合约面值(张) * (1 / 标记价格 - 1 / 开仓均价)
        // 收益率 = 未实现盈亏 / 仓位保证金

        if (markPrice && contractSize) {
          // 修复：直接使用带单数据计算，不依赖持仓列表匹配
          const floatProfit = getFloatProfit({
            symbol: item.symbolName,
            entryPrice: item.openPrice, // 使用带单的开仓价
            positionSize: item.positionSize, // 使用当前持仓数量
            positionSide,
          } as PositionItem);

          const floatProfitForMarkPrice = getFloatProfit({
            symbol: item.symbolName,
            entryPrice: item.openPrice,
            positionSize: item.positionSize,
            positionSide,
          } as PositionItem);

          item.floatProfit = floatProfit;
          item.floatProfitForMarkPrice = floatProfitForMarkPrice;
          item.floatProfitRate = new BigNumber(floatProfit)
            .div(isolatedMargin)
            .times(100)
            .valueOf();

          // 调试日志：方便排查问题
          // console.log('[带单盈亏计算]', {
          //   symbolName: item.symbolName,
          //   openPrice: item.openPrice,
          //   markPrice,
          //   positionSize: item.positionSize,
          //   positionSide,
          //   floatProfit,
          //   floatProfitRate: item.floatProfitRate,
          //   openMargin: isolatedMargin,
          // });
        }
      }

      return item;
    });
    return alignLiquidationPrices(calculatedList);
  }

  @computed get currentFollowOrderListWithConfig() {
    const getSymbolConfig = this.rootStore.ticker.getSymbolConfig;
    const getPrecision = this.rootStore.ticker.getPrecision;
    const symbolMarkPriceMap = this.rootStore.ticker.markPriceMap;
    const symbolCurrentPriceMap = this.rootStore.ticker.symbolCurrentPriceMap;
    const currentFollowOrderList = cloneDeep(this.currentFollowOrderList);
    const getFloatProfit = this.getFloatProfit;
    const getLiquidationPrice = this.getLiquidationPrice;
    const positionList = cloneDeep(this.rootStore.position.positionList);
    // 跟单数据来源：通过 fetchCurrentFollowOrderList 调用 API.FOLLOWER_ORDER_LIST 获取
    // 跟单余额：balanceList 中 balanceType === 'COPY' 的项的 availableBalance
    // 强平价依赖跟单持仓与跟单余额（getDex），显式读取以建立 MobX 依赖，持仓/余额变化时本 computed 会重算、视图会更新
    const balanceList = this.rootStore.asset.balanceList;
    // 显式读取 balanceList 的长度和内容，确保 MobX 能追踪到变化
    void balanceList.length;
    // 遍历 balanceList 读取每个项的属性，确保当 balanceList 更新时 computed 会重新计算
    balanceList.forEach((item: any) => {
      if (item.balanceType === 'COPY') {
        // 深入读取跟单余额的所有相关属性，确保 MobX 能追踪到变化
        void item.availableBalance;
        void item.walletBalance;
        void item.isolatedMargin;
        void item.openOrderMarginFrozen;
      }
    });
    const copyBalance = balanceList.find((item: any) => item.balanceType === 'COPY');
    void currentFollowOrderList.length;
    // 显式触发对跟单标记价/最新价的依赖，确保预估强平价随价格刷新
    currentFollowOrderList.forEach((item: FollowOrderItem) => {
      if (item?.symbolName) {
        void symbolMarkPriceMap[item.symbolName];
        void symbolCurrentPriceMap[item.symbolName];
      }
    });

    // 将跟单列表转换为 PositionItem 格式，用于计算全仓维持保证金和浮动盈亏（dex）
    const getFollowUnsettledProfit = (item: FollowOrderItem) => {
      if (!item) {
        return '0';
      }
      const matchById = positionList.find((p: PositionItem) => p.positionId === item.positionId);
      if (matchById?.unsettledProfit) {
        return matchById.unsettledProfit;
      }
      const matchByKey = positionList.find(
        (p: PositionItem) =>
          p.symbol === item.symbolName &&
          p.positionSide === item.positionSide &&
          p.positionType === item.positionType &&
          (p.balanceType === 'COPY' || !p.balanceType),
      );
      return matchByKey?.unsettledProfit || '0';
    };

    const followPositionList: PositionItem[] = currentFollowOrderList
      .filter((item: FollowOrderItem) => item.symbolName && new BigNumber(item.positionSize || 0).gt(0))
      .map((item: FollowOrderItem) => ({
        symbol: item.symbolName,
        entryPrice: item.openPrice,
        isolatedMargin: item.openMargin,
        positionSize: item.positionSize,
        positionSide: item.positionSide,
        positionType: item.positionType,
        positionModel: item.positionModel,
        unsettledProfit: getFollowUnsettledProfit(item),
        leverage: item.openLeverage,
      } as PositionItem));

    const calculatedList = currentFollowOrderList.map((item: FollowOrderItem) => {
      // 添加本地计算值
      item.floatProfit = '0.00';
      item.floatProfitRate = '0.00';
      item.floatProfitForMarkPrice = '0.00';
      item.liquidationPrice = '--';

      const { symbolName: symbol, openMargin: isolatedMargin, positionSide } = item;

      // 跳过无效数据
      if (!symbol || !new BigNumber(item.positionSize || 0).gt(0)) {
        return item;
      }

      // 跟单若未带仓位模式，从持仓回填，保证逐仓分仓不强行对齐强平价
      if (!item.positionModel) {
        const matched = positionList.find(
          (p: PositionItem) =>
            String(p.positionId) === String(item.positionId) ||
            (p.symbol === item.symbolName &&
              p.positionSide === item.positionSide &&
              p.positionType === item.positionType &&
              (p.balanceType === 'COPY' || !p.balanceType)),
        );
        if (matched?.positionModel) item.positionModel = matched.positionModel;
      }

      // 预估强平价：复用持仓强平价逻辑，用跟单数据构造等价 PositionItem
      // 使用跟单账户余额（useCopyBalance=true）和跟单持仓列表（followPositionList）计算 dex
      // 确保强平价基于跟单账户的余额和持仓，而非主账户
      const positionLike = {
        symbol: item.symbolName,
        entryPrice: item.openPrice,
        isolatedMargin: item.openMargin,
        positionSize: item.positionSize,
        positionSide: item.positionSide,
        positionType: item.positionType,
        positionModel: item.positionModel,
        unsettledProfit: getFollowUnsettledProfit(item),
        leverage: item.openLeverage,
      } as PositionItem;
      // 跟单强平价：按单仓位计算（同 symbol 不再共用同一强平价）
      // 注意：即使 copyBalance 不存在，也会尝试计算（使用 walletBalance 作为后备）
      try {
        item.liquidationPrice = getLiquidationPrice(positionLike, followPositionList, true, true);
        // 调试：如果强平价为 '--'，可能是数据不完整
        // console.log('[跟单强平价计算]', {
        //   liquidationPrice: item.liquidationPrice,
        // });
      } catch (error) {
        // console.error('[跟单强平价计算错误]', { symbol, error, item });
        item.liquidationPrice = '--';
      }

      const symbolConfig = getSymbolConfig(symbol);
      const markPrice = symbolMarkPriceMap[symbol];

      item.markPrice = markPrice;

      if (symbolConfig) {
        const { contractSize, pricePrecision, quoteCoinDisplayPrecision } = symbolConfig;
        const { quantity: quantityPrecision } = getPrecision(symbol);

        // 添加其他配置信息
        item.contractSize = contractSize;
        item.quantityPrecision = quantityPrecision; // 数量精度
        item.pricePrecision = pricePrecision; // 价格精度
        item.quoteCoinDisplayPrecision = quoteCoinDisplayPrecision; // 计价币种精度

        // 浮动盈亏
        // U本位做多：数量(张) * 合约面值(张) * (标记价格-开仓均价)；
        // U本位做空：数量(张) * 合约面值(张) * (开仓均价-标记价格)；
        // 币本位做多：数量(张) * 合约面值(张) * (1 / 开仓均价 - 1 / 标记价格)
        // 币本位做空：数量(张) * 合约面值(张) * (1 / 标记价格 - 1 / 开仓均价)
        // 收益率 = 未实现盈亏 / 仓位保证金

        if (markPrice && contractSize) {
          // 修复：直接使用跟单数据计算，不依赖持仓列表匹配
          const floatProfit = getFloatProfit({
            symbol: item.symbolName,
            entryPrice: item.openPrice, // 使用跟单的开仓价
            positionSize: item.positionSize, // 使用当前持仓数量
            positionSide,
          } as PositionItem);

          const floatProfitForMarkPrice = getFloatProfit({
            symbol: item.symbolName,
            entryPrice: item.openPrice,
            positionSize: item.positionSize,
            positionSide,
          } as PositionItem);

          item.floatProfit = floatProfit;
          item.floatProfitForMarkPrice = floatProfitForMarkPrice;
          item.floatProfitRate = new BigNumber(floatProfit)
            .div(isolatedMargin)
            .times(100)
            .valueOf();

          // 调试日志：方便排查问题
          // console.log('[跟单盈亏计算]', {
          //   symbolName: item.symbolName,
          //   openPrice: item.openPrice,
          //   markPrice,
          //   positionSize: item.positionSize,
          //   positionSide,
          //   floatProfit,
          //   floatProfitRate: item.floatProfitRate,
          //   openMargin: isolatedMargin,
          // });
        }
      }

      return item;
    });
    return alignLiquidationPrices(calculatedList);
  }

  // 合约操作主界面的跟单列表
  @action
  setLeaderOrderList(data: LeaderOrderItem[]) {
    this.leaderHomeOrderList = data;
  }

  @action.bound
  setLeaderOrderListPageInfo(data: PageInfo) {
    const origin = { ...this.leaderHomeOrderList };
    this.leaderHomeOrderListPageInfo = { ...origin, ...data };
  }

  // 合约操作主界面的跟单列表
  @action.bound
  async fetchLeaderOrderList(params: { type?: number; symbol?: string; page?: number; size?: number }) {
    const pageInfo = this.leaderHomeOrderListPageInfo;

    const result: any = await Axios.get(API.LEADER_ORDER_LIST, {
      params: {
        page: pageInfo.page,
        size: pageInfo.size,
        type: 1,
        ...params,
      },
    });

    if (result) {
      // const { items, page, ps: size, total } = result;
      if (Array.isArray(result)) {
        this.setLeaderOrderList(result);
      }
      //
      // this.setLeaderOrderList(items);
      // this.setLeaderOrderListPageInfo({ page, size, total });
    }
  }

  @action.bound
  updateLeaderOrderListByRow = (params: any) => {
    const { orderId } = params;
    const index = this.leaderHomeOrderList.findIndex((item: any) => item.orderId === orderId);

    const newList: any = [...this.leaderHomeOrderList];
    if (index > -1) {
      if (!params.positionSize || new BigNumber(params.positionSize).lte(0)) {
        newList.splice(index, 1);
        const { page, size, total } = this.leaderHomeOrderListPageInfo;
        this.setLeaderOrderListPageInfo({ page, size, total: total - 1 });
      } else {
        newList.splice(index, 1, params);
      }
    } else {
      newList.unshift(params);
      const { page, size, total } = this.leaderHomeOrderListPageInfo;
      this.setLeaderOrderListPageInfo({ page, size, total: total + 1 });
    }
    this.setLeaderOrderList(newList);
  };

  // 查询当前跟单(主页面)
  @action
  setCurrentFollowOrderList(data: FollowOrderItem[]) {
    this.currentFollowOrderList = data;
  }

  @action.bound
  async fetchCurrentFollowOrderList(params: { symbol?: string }) {
    const result: any = await Axios.get(API.FOLLOWER_ORDER_LIST, {
      params: {
        type: 1,
        ...params,
      },
    });

    if (Array.isArray(result)) {
      this.setCurrentFollowOrderList(result);
    }
  }

  @action.bound
  updateFollowOrderListByRow = (params: any) => {
    const { orderId } = params;
    const index = this.currentFollowOrderList.findIndex((item: any) => item.orderId === orderId);

    const newList: any = [...this.currentFollowOrderList];
    if (index > -1) {
      if (!params.positionSize || new BigNumber(params.positionSize).lte(0)) {
        newList.splice(index, 1);
      } else {
        newList.splice(index, 1, params);
      }
    } else {
      newList.unshift(params);
    }

    this.setCurrentFollowOrderList(newList);
  };

  @action.bound
  isWithdrawingFollowOrder(id: string) {
    return this.withdrawFollowOrderList.includes(id);
  }

  @action.bound
  setWithdrawFollowOrderList(data: string[]) {
    this.withdrawFollowOrderList = data;
  }

  @action.bound
  setWithdrawLeaderOrderList(data: string[]) {
    this.withdrawLeaderOrderList = data;
  }

  @action.bound
  async fetchCancelLeaderOrder(params: { trackNo: string; orderId: string }) {
    const { trackNo, orderId } = params;
    this.setWithdrawLeaderOrderList([...this.withdrawLeaderOrderList, orderId]);
    const result: any = await Axios.post(API.LEADER_ORDER_CLOSE, {
      leaderOrder: true,
      trackNo,
    }).catch(({ error }: any) => { });

    if (result) {
    }

    this.setWithdrawLeaderOrderList([...this.withdrawLeaderOrderList, orderId]);
  }

  @action.bound
  async fetchCancelFollowOrder(params: { positionId: string; leaderOrder: boolean }) {
    const { positionId, leaderOrder } = params;
    this.setWithdrawFollowOrderList([...this.withdrawFollowOrderList, positionId]);
    const result: any = await Axios.post(API.FOLLOWER_ORDER_CLOSE, {
      leaderOrder,
      positionId,
    }).catch(({ error }: any) => {
      // @ts-ignore
    });

    if (result) {
      // 通过ws更新list
      // const newList = targetPage === 'PositionPage' ? [...this.currentFollowOrderList] : [...this.myFollowOrderList];
      // const index = newList.findIndex((item: FollowOrderItem) => orderId === item.orderId);
      // newList.splice(index, 1);
      // if (targetPage === 'PositionPage') {
      //   this.setCurrentFollowOrderList(newList);
      // } else if (targetPage === 'FollowPage') {
      //   this.setMyFollowOrderList(newList);
      // }
    }

    this.setWithdrawFollowOrderList([...this.withdrawFollowOrderList, positionId]);
  }

  @action.bound
  async fetchCopyUpdateProfitStop(params: {
    positionId: string;
    triggerPriceType: string;
    leaderOrder: boolean;
    triggerProfitPrice: string;
    triggerStopPrice: string;
  }) {
    const { positionId, triggerPriceType, leaderOrder, triggerProfitPrice, triggerStopPrice } = params;

    const result: any = await Axios.post(API.FOLLOWER_ORDER_STOP, {
      positionId,
      triggerPriceType,
      leaderOrder,
      triggerProfitPrice,
      triggerStopPrice,
    }).catch(({ error }: any) => { });

    if (result) {
    }
  }

  @action.bound
  refreshFollowListKey() {
    this.followListRefreshKey += 1;
  }

  @action.bound
  setLeaderSymbolList(data: string[]) {
    this.leaderSymbolList = data;
  }

  @action.bound
  async fetchLeaderSymbolList() {
    const result: any = await Axios.get(API.LEADERSYMBOL, {});
    // const { result } = res;
    if (result) {
      this.setLeaderSymbolList(result);
    }
  }
  // 合约下单是否需要二次确认的开关
  @action.bound
  setIsConfirmAgain(data: boolean) {
    console.log('setIsConfirmAgain', data);
    this.isConfirmAgain = data;
  }
}
